import { create } from 'zustand';
import { useSpaceStore } from '../../../../stores/spaceStore';
import { AnyWindowEntity } from '../../../../types/windows';
import { EventEmitter } from 'events';
import { WindowContent, SpaceContext } from '../types';
import useEntityStore from '../../../../stores/entityStore';
import { debounce } from 'lodash';

import { userTrackingService } from '../../../../services/users/userTrackingService';

import { getAppTypeByEntity, getWindowTitle } from '../../utils/windowUtils';
import { createFileLogger, formatJSON } from '../../../../utils/logger';
import { entityResolver } from '../../../../services/entityResolver';
import { docsCRDTService } from '../../../../apps/DocsApp/services/crdt/docsCRDTService';
import { YjsCommands } from '../../../../apps/DocsApp/commands/v1/utils';
import spaceStore from '../../../../stores/spaceStore';
import { isContextEligibleEntity, getContextAppType } from '../../../../utils/entityContextEligibility';
import { createContextActions } from './contextActions';
import { 
  EntityReference, 
  ExplicitContent, 
  ReferenceOnlyContext,
  SubmitLifecycle
} from '../types/referenceOnlyContext';
// Set logging enabled/disabled at the file level (no need to edit logger.ts!)
const log = createFileLogger(undefined, { enabled: true });


interface PastedContext {
  text: string;
  structuredContent?: any;
  source: {
    window: AnyWindowEntity;
    type: string;
    metadata: {
      docId: string;
      nodeRange?: {
        startNode: string;
        endNode: string;
        isSameNode: boolean;
        startNodeChar?: number;
        endNodeChar?: number;
        selectionFrom?: number;
        selectionTo?: number;
      };
      hasStructuredContent?: boolean;
      structuredContentSelector?: any;
      originalText?: string;
      originalRange?: {
        from: string;
        to: string;
        isSameNode: boolean;
        totalNodes: number;
      };
      pastedAt?: string;
      wordCount?: number;
      charCount?: number;
      extractionMethod?: string;
      structuralInfo?: any; // Structural formatting information from doc_read
      formattingHints?: string[]; // Hints for preserving formatting
      hasComplexFormatting?: boolean; // Flag for complex formatting presence
    };
  };
}

interface WebsiteContext {
  url: string;
  title: string;
  description?: string;
  siteName?: string;
  content: string; // Main text content/excerpt
  metadata: {
    faviconUrl?: string;
    previewImageUrl?: string;
    wordCount?: number;
    fetchedAt: string;
    source: string; // 'og', 'readability', 'fallback', etc.
    contentLength?: number;
  };
  addedAt: string;
  addedBy: 'auto' | 'manual'; // How the link was added
}

interface EntityContext {
  entityId: string;
  entityType: string;
  name: string;
  llmId?: string;
  content: string; // Entity content preview or description
  systemPromptHint?: string; // Optional text injected into system prompt TIER 4
  metadata: {
    addedBy: 'drag' | 'manual' | 'automatic';
    entitySize?: number; // For docs, number of nodes/words
    lastModified?: string;
    contentType?: string; // 'document', 'pdf', 'spreadsheet', etc.
    isProcessed?: boolean; // For PDFs - whether they've been enriched
    [key: string]: any;
  };
  addedAt: string;
}

interface FileContext {
  fileName: string;
  fileSize: number;
  fileType: string;
  mimeType: string;
  content: string; // File content preview or metadata
  file?: File; // Store the actual File object for upload
  metadata: {
    source: 'external' | 'upload';
    lastModified: number;
    addedBy: 'drag' | 'manual';
    contentPreview?: string; // First few lines of text files
    contentType?: string; // MIME type or content type identifier
    // Upload state tracking (NEW)
    uploadId?: string; // Unique ID for this upload (solves duplicate filename issue)
    isUploading?: boolean; // Is currently uploading
    uploadProgress?: number; // 0-100
    uploadError?: string; // Error message if upload failed
    uploadStartTime?: number; // Timestamp when upload started (for timeout detection)
    // Entity tracking (after upload)
    entityId?: string; // PDF entity ID after upload
    llmId?: string; // PDF llmId after upload
    isProcessed?: boolean; // For PDFs - whether they've been enriched
  };
  addedAt: string;
}

interface ContextStore {
  context: SpaceContext;
  setActiveSpace: (spaceId: string) => void;
  // These are replaced by the new _handleActiveWindowChange() system

  cleanup?: () => void;
  dispose: () => void;
  
  // Web search flag
  webSearchEnabled: boolean;
  toggleWebSearch: () => void;
  setWebSearch: (enabled: boolean) => void;
  
  // 🏗️ NEW: Reference-Only System (eliminates staleness)
  entityReferences: EntityReference[];
  explicitContent: ExplicitContent[];
  currentSubmit: SubmitLifecycle | null;
  _lastLocalModification: number | null;
  _markLocalModification: () => void;
  
  // Text excerpt handling (for pasted/selected text)
  addTextExcerpt: (excerpt: PastedContext) => Promise<void>;
  
  
    // 🧠 NEW: Document structure analysis for semantic understanding
    analyzeDocumentStructure: (nodes: any[]) => any;
    
    // 🏗️ NEW: Reference-Only Context Building
    buildReferenceOnlyContext: () => ReferenceOnlyContext;
    
    // 🏗️ NEW: Reference-Only Document Management
    addEntityReference: (llmId: string, metadata: Partial<EntityReference>) => void;
    removeEntityReference: (llmId: string, options?: { force?: boolean; reason?: string }) => void;
    addCurrentDocumentToContext: () => void;
    getEntityReferences: () => EntityReference[];
    hasEntityReference: (llmId: string) => boolean;
    
    // 🏗️ NEW: Explicit Content Management
    addExplicitContent: (content: Omit<ExplicitContent, 'id' | 'addedAt'>) => string;
    updateExplicitContent: (id: string, updates: Partial<Omit<ExplicitContent, 'id' | 'addedAt'>>) => void;
    removeExplicitContent: (id: string) => void;
    clearExplicitContent: () => void;
    getExplicitContent: () => ExplicitContent[];
    
    // 🏗️ NEW: Submit Lifecycle
    onSubmitStarting: () => void;
    onSubmitComplete: () => void;
    onSubmitFailed: () => void;


  // Entity context methods (for entity pills)
  entityContexts: EntityContext[];
  addEntityContext: (entityContext: EntityContext) => void;
  removeEntityContext: (entityId: string) => void;
  clearEntityContexts: () => void;
  getEntityContexts: () => EntityContext[];
  hasEntityContext: (entityId: string) => boolean;
  markPDFAsProcessed: (entityId: string) => void;

  // Simple upsert API for apps/entities to inject context programmatically.
  // Adds the entry if it doesn't exist, updates it in-place if it does.
  // Call clearEntityContextContribution on unmount for cleanup.
  setEntityContextContribution: (entityId: string, contribution: {
    entityType: string;
    name: string;
    llmId?: string;
    content: string;
    systemPromptHint?: string;
    metadata?: Partial<EntityContext['metadata']>;
  }) => void;
  clearEntityContextContribution: (entityId: string) => void;

  // File context methods (for file pills)
  fileContexts: FileContext[];
  addFileContext: (fileContext: FileContext) => void;
  removeFileContext: (fileName: string) => void;
  clearFileContexts: () => void;
  getFileContexts: () => FileContext[];
  hasFileContext: (fileName: string) => boolean;
  // Upload state management (NEW)
  updateFileContextProgress: (uploadId: string, progress: number) => void;
  updateFileContextComplete: (uploadId: string, entity: any) => void;
  updateFileContextError: (uploadId: string, error: string) => void;
  updateFileContextWithLlmId: (entityId: string, llmId: string) => void; // NEW: Update llmId after enrichment

  // Unified context clearing
  clearAllContext: () => void; // Clear everything (excerpts + websites + refs)

  setContext: (newContext: SpaceContext) => void;
  // Active window changes are now handled by _handleActiveWindowChange()
  
  // Get tool execution context (replaces ContextBuilder)
  getToolExecutionContext: (params: {
    userId: string;
    threadId: string;
    thread: any;
    model: string;
  }) => any;
  
  // Token management (replaces ContextPriorityManager)
  optimizeContextForTokens: (maxTokens?: number) => SpaceContext;

  // NEW: Simple API for apps to notify when they're ready
  notifyAppReady: (windowId: string, metadata?: { appType?: string; contentLength?: number }) => void;
  notifyContentUpdate: (windowId: string, metadata?: { contentLength?: number; source?: string }) => void;
  
  // Rules system
  getActiveRules: () => Promise<any[]>;
  
  // ====== INTERNAL HELPERS (Single Mutation Funnel) ======
  // Following entityStore pattern - all mutations go through these helpers
  
  _applyAddEntityReference: (llmId: string, options: {
    title: string;
    appType: string;
    entityType: string;
    windowId?: string;
    documentRole: 'currentDoc' | 'contextDoc';
    isActive: boolean;
    currentSlide?: number;
    totalSlides?: number;
  }) => void;
  
  _applyRemoveEntityReference: (llmId: string, options: {
    reason: string;
  }) => void;
  
  _removeAllCurrentDocs: () => void;
  _handleActiveWindowChange: (windowId: string | null, isRetry?: boolean) => void;
  updatePresentationSlide: (entityId: string, currentSlide: number, totalSlides: number) => void;
  
  _applyAddExplicitContent: (content: {
    text: string;
    sourceType: 'selection' | 'paste';
    sourceDocument?: { llmId: string; title: string };
    clearAfterSubmit?: boolean;
    metadata?: any;
  }) => string;
  
  _applyRemoveExplicitContent: (id: string) => void;
  
  _applyAddEntityContext: (entityContext: {
    entityId: string;
    entityType: string;
    name: string;
    llmId?: string;
    content: string;
    metadata?: any;
  }) => void;
  
  _applyRemoveEntityContext: (entityId: string) => void;
  
  _promoteToContextDoc: (llmId: string) => void;
  _promoteEntityContextToManual: (entityId: string) => void;
  
  _applyAddFileContext: (fileContext: {
    fileName: string;
    fileSize: number;
    fileType: string;
    mimeType: string;
    content: string;
    file?: File;
    metadata?: any;
  }) => void;
  
  _applyRemoveFileContext: (fileName: string) => void;
  
  // Actions layer (orchestration)
  actions: ReturnType<typeof import('./contextActions').createContextActions>;
}

// Create a singleton event emitter instance
const contextEventEmitter = new EventEmitter();
// Increase listener cap to support multiple Docs windows without warnings
try { contextEventEmitter.setMaxListeners(50); } catch {}

// Expose the event emitter globally for plugins to access
if (typeof window !== 'undefined') {
  (window as any).contextEventEmitter = contextEventEmitter;
}



// Removed hardcoded tool suggestions - AI can determine appropriate tools based on entity type and context

// Helper to ensure windows are always minimal
// Extract meaningful text content from window content
const extractWindowText = (windowContent: any): string => {
  // For docs windows, prefer structured content
  if (windowContent.appType === 'docs' && windowContent.documentStructure?.nodes) {
    return windowContent.documentStructure.nodes
      .map((node: any) => node.text || '')
      .join(' ');
  }
  // For other content, use the content field
  return windowContent.content || '';
};

/**
 * Attempt to add a full-document excerpt for a Docs entity if one doesn't already exist.
 * - Uses doc_read_v2 to fetch full Markdown
 * - Creates a PastedContext-shaped excerpt with source metadata llmId = entity.llmId
 * - No-op if an excerpt for the same docId already exists
 */
/**
 * Creates window content from a window entity with all metadata and flags.
 * This is the single unified function for creating WindowContent objects.
 * 
 * @param window - The window entity to create content from
 * @param options - Configuration options including flags and behaviors
 * @returns WindowContent object or null if creation fails
 */
const createWindowContent = async (
  window: AnyWindowEntity,
  options: {
    isManuallySet?: boolean;
    isActive?: boolean;
    preserveFlags?: { isActive?: boolean; isManuallySet?: boolean };
  } = {}
): Promise<WindowContent | null> => {
  log.log(`[contextStore] Creating window content for window ${window.id}`);

  const windowId = window.id;
  const title = window.type === 'window' ? getWindowTitle(window) : window.title || 'Untitled';
  const appType = window.appType || '';
  
  // Single entity lookup - used throughout the function
  const entity = useSpaceStore.getState().getWindowEntity(windowId);
  
  // Build the window content based on app type
  let windowContent: WindowContent | null = null;

  // Handle PDFium (PDF reader) windows
  if (appType === 'pdfium' || window.applicationState?.pdfium) {
    log.log(`[contextStore] Detected PDF window: ${windowId}`);

    let entityId: string | null = window.applicationState?.pdfium?.entityId || null;
    if (!entityId && entity) {
      entityId = (entity as any)._id || (entity as any).id || null;
    }

    // Generate minimal preview to avoid overloading prompts with large PDFs
    let partialContent = '';
    if (entity?.skeleton?.enrichments) {
      const enrichments = entity.skeleton.enrichments;
      if (Array.isArray(enrichments) && enrichments.length > 0) {
        // Extract basic metadata only - no content to prevent prompt bloat
        const totalPages = enrichments.length;
        const firstPageText = enrichments[0]?.text || enrichments[0]?.content || '';
        const preview = firstPageText.slice(0, 200); // Max 200 chars preview
        
        partialContent = `[PDF Document: ${totalPages} page${totalPages !== 1 ? 's' : ''} enriched and ready]
First page preview: "${preview}${firstPageText.length > 200 ? '...' : ''}"

📖 To read this PDF:
- Use doc_read_v2 with llmId to get the full document content
- The PDF has been processed and is ready to read`;
      }
    }

    windowContent = {
      appType: 'pdfium', // Keep 'pdfium' for entityRefs - PDF commands expect this
      windowId,
      entityId: entityId || undefined,
      isActive: false, // Will be set later
      stableId: `pdf-${entityId || windowId}`,
      title,
      content: partialContent,
      contentHash: entity?.skeleton?.enrichments ? String(Date.now()) : '',
      isManuallySet: false // Will be set later
    };
    
    // Add enhanced metadata for PDF windows
    if (window.applicationState?.pdfium?.entityId) {
      (windowContent as any).entityId = window.applicationState.pdfium.entityId;
    }
  }

  // Handle docs windows
  if (appType === 'docs' || (window.applicationState as any)?.docs) {
    log.log(`[contextStore] Detected docs window: ${windowId}`);

    let entityLlmId = null;
    if ((window.applicationState as any)?.docs?.activeDocId) {
      entityLlmId = (window.applicationState as any).docs.activeDocId;
    } else if ((window as any).docId) {
      entityLlmId = (window as any).docId;
    }

    const docsState = (window.applicationState as any)?.docs;
    let documentStructure = null;
    let actualLlmId = entityLlmId || entity?.llmId;
    let spaceStoreEntity = null;
    let yDoc = null;
    
    // First, try to get entity from SpaceStore using entityLlmId
      try {
        const entityStore = useEntityStore.getState();
      
      // Look for entity by entityLlmId from window
      if ((window as any).entityLlmId) {
        spaceStoreEntity = entityStore.entities.find((e: any) => e.llmId === (window as any).entityLlmId);
      }
      
      // If not found by entityLlmId, try to find by llmId
      if (!spaceStoreEntity && actualLlmId) {
        spaceStoreEntity = entityStore.entities.find((e: any) => e.llmId === actualLlmId);
      }
    } catch (error) {
      console.warn(`[contextStore] ⚠️ Failed to access SpaceStore:`, error);
    }
    if (actualLlmId) {
      try {
        const mockContext = {
          spaceId: 'current-space',
          executionId: 'context-store-access',
          userId: 'current-user'
        };
        
        // Use entityResolver for consistent ID resolution (synchronous cache lookup)
        // Note: We use resolveFromCache to avoid async issues in the contextStore
        const resolved = (entityResolver as any).resolveFromCache?.(actualLlmId);
        
        // Since contextStore is synchronous, we need to use the entity data we have
        // and let the Y.js document be resolved through the normal CRDT path
        // but with the correct resolved document ID
        if (resolved && resolved.entity) {
          const resolvedDocId = resolved.entity._id || resolved.entity.id || actualLlmId;
          actualLlmId = resolvedDocId; // Use the resolved ID consistently
          log.log(`[contextStore] ✅ Resolved document ID via entityResolver: ${resolvedDocId}`);
        }
      } catch (error) {
        console.warn(`[contextStore] ⚠️ entityResolver failed, using original ID:`, error);
      }
      // This prevents empty document initialization bug on reload
      let initialBase64: string | undefined;
      let initialContent: any | undefined;
      
      try {
        log.log(`[contextStore] 🔄 Fetching document entity with content for: ${actualLlmId.slice(-8)}`);
        
        // Fetch the full document entity (includes base64/content)
        const docEntity = await useEntityStore.getState().actions.getEntity(actualLlmId);
        
        if (docEntity?.skeleton) {
          initialBase64 = docEntity.skeleton.base64;
          initialContent = docEntity.skeleton.content;
          
          log.log(`[contextStore] ✅ Fetched document content:`, {
            docId: actualLlmId.slice(-8),
            hasBase64: !!initialBase64,
            base64Length: initialBase64?.length || 0,
            hasContent: !!initialContent,
            contentType: typeof initialContent,
            name: docEntity.name
          });
        } else {
          console.warn(`[contextStore] ⚠️ Document entity has no skeleton:`, actualLlmId.slice(-8));
        }
      } catch (fetchError) {
        console.warn(`[contextStore] ⚠️ Failed to fetch document entity, will initialize empty:`, fetchError);
      }
      
      // Check if Y.js document is already initialized (by DocsApp)
      // DON'T initialize it here - that's the DocsApp's job
      try {
        
        // Only get the cached document if it already exists
        const cachedPromise = docsCRDTService.getCachedYDoc?.(actualLlmId);
        if (cachedPromise) {
          const cached = await cachedPromise;
          yDoc = cached.doc;
          log.log(`[contextStore] ✅ Found cached Y.js document: ${actualLlmId}`);
        } else {
          log.log(`[contextStore] ⏳ Document not yet initialized, will load when DocsApp opens it: ${actualLlmId}`);
        }
      } catch (crdtError) {
        console.warn(`[contextStore] ⚠️ Failed to check CRDT cache:`, crdtError);
      }
    }
    if (yDoc) {
      try {
        // Import YjsCommands synchronously since this is in a Zustand store
        
        // Create document context - EXACT same structure as doc_read.ts
        const docContext = {
          yxml: yDoc.getXmlFragment('prosemirror'),
          doc: yDoc,
          docId: actualLlmId,
          userId: 'current-user',
          metadata: { 
            windowId: windowId || '', 
            spaceId: 'current-space', 
            documentName: spaceStoreEntity?.name || entity?.name || 'document'
          }
        };
        
        // Read all nodes using YjsCommands - same as doc_read.ts
        const allNodesResult = YjsCommands.read.all(docContext);
        
        if (allNodesResult?.nodes && allNodesResult.nodes.length > 0) {
          // Store Y.js nodes directly - no raw content extraction
          documentStructure = {
            nodes: allNodesResult.nodes
          };
          
          log.log(`[contextStore] ✅ Got document structure with ${allNodesResult.nodes.length} Y.js nodes`);
        } else {
          log.log(`[contextStore] ⚠️ No nodes found in Y.js document`);
        }
      } catch (error) {
        console.warn(`[contextStore] ⚠️ Failed to read Y.js document nodes:`, error);
      }
    }

    log.log(`[CONTEXT DEBUG] Final result for ${windowId}:`, {
      hasDocumentStructure: !!documentStructure,
      nodesCount: documentStructure?.nodes?.length || 0
    });

    // Extract text content from nodes for backward compatibility
    const textContent = documentStructure?.nodes 
      ? documentStructure.nodes.map((node: any) => node.text || '').join(' ')
      : '';
    
    const windowContent = {
      appType: 'docs' as const,
      windowId,
      entityLlmId: actualLlmId,
      llmId: actualLlmId,
      docId: actualLlmId,
      screen: 'editor',
      isActive: false, // Will be set later based on options
      title: spaceStoreEntity?.name || docsState?.currentDocName || entity?.name || entity?.title || 'Untitled Document',
      content: textContent,
      contentHash: documentStructure?.nodes ? String(Date.now()) : '',
      documentStructure: documentStructure || undefined,
      stableId: `docs-${actualLlmId}`,
      isManuallySet: false // Will be set later based on options
    };
    
    // Apply enhancements for docs windows
    log.log(`[CONTEXT DEBUG] Enhanced window content - DocsApp initialized for ${windowId}:`, {
      hasDocsState: !!docsState,
      currentDocName: docsState?.currentDocName,
      currentState: docsState?.currentState,
      hasDocId: !!windowContent.docId,
      nodeCount: documentStructure?.nodes?.length || 0
    });
    
    // Update title from DocsApp state if available
    if (docsState?.currentDocName) {
      windowContent.title = docsState.currentDocName;
    }
    
    // Add enhanced metadata
    (windowContent as any).capabilities = ['edit', 'insert', 'load'];
    (windowContent as any).windowStatus = docsState?.activeDocId ? 'ready' : 'initializing';
  }

  // Handle note windows
  if (appType === 'notes' || appType === 'note') {
    log.log(`[contextStore] Detected note window: ${windowId}`);

    let partialContent = '';
    let contentHash = '';

    if ((window.applicationState as any)?.notes?.content) {
      const fullContent = (window.applicationState as any).notes.content;
      const preview = fullContent.substring(0, 500);
      const totalLength = fullContent.length;
      partialContent = `${preview}${fullContent.length > 500 ? '...' : ''}\n\n[Note has ${totalLength} characters. Use notes tools to access specific sections and content.]`;
      contentHash = String(Date.now());
    }

    windowContent = {
      appType: 'notes',
      windowId,
      isActive: false, // Will be set later
      stableId: `notes-${windowId}`,
      title,
      content: partialContent,
      contentHash,
      isManuallySet: false // Will be set later
    };
  }

  // Handle Prompt entities
  if (appType === 'prompts-v2' && entity?.skeleton) {
    log.log(`[contextStore] Detected Prompt entity: ${windowId}`);

    const promptSkeleton = entity.skeleton;
    let promptContent = '';
    
    // Build detailed prompt content for LLM context
    if (promptSkeleton.content) {
      promptContent = `**Prompt Content:**\n${promptSkeleton.content}`;
    }
    
    if (promptSkeleton.description) {
      promptContent += `\n\n**Description:** ${promptSkeleton.description}`;
    }
    
    if (promptSkeleton.category) {
      promptContent += `\n**Category:** ${promptSkeleton.category}`;
    }
    
    if (promptSkeleton.tags && Array.isArray(promptSkeleton.tags) && promptSkeleton.tags.length > 0) {
      promptContent += `\n**Tags:** ${promptSkeleton.tags.join(', ')}`;
    }
    
    if (promptSkeleton.inputVariables && Array.isArray(promptSkeleton.inputVariables) && promptSkeleton.inputVariables.length > 0) {
      promptContent += `\n**Input Variables:** ${promptSkeleton.inputVariables.join(', ')}`;
    }
    
    if (promptSkeleton.expectedOutput) {
      promptContent += `\n**Expected Output:** ${promptSkeleton.expectedOutput}`;
    }
    
    if (promptSkeleton.usageInstructions) {
      promptContent += `\n**Usage Instructions:** ${promptSkeleton.usageInstructions}`;
    }

    windowContent = {
      appType: 'prompts-v2',
      windowId,
      isActive: false, // Will be set later
      stableId: `prompt-${windowId}`,
      title,
      content: promptContent,
      entityName: entity.name,
      contentHash: promptContent ? String(promptContent.length) + promptContent.slice(0, 32) : '',
      isManuallySet: false // Will be set later
    };
  }

  // Generic fallback for unknown window types
  if (!windowContent) {
    log.log(`[contextStore] Unknown window type: ${appType} for window ${windowId}`);
    windowContent = {
      appType: appType || 'unknown',
      windowId,
      isActive: false, // Will be set later
      stableId: `window-${windowId}`,
      title,
      isManuallySet: false // Will be set later
    };
  }
  
  // ======================================================================
  // FINALIZATION: Apply flags and overrides
  // ======================================================================
  if (!windowContent) {
    return null;
  }
  
  // Apply flag overrides based on options
  if (options.preserveFlags) {
    windowContent.isActive = options.preserveFlags.isActive ?? windowContent.isActive;
    windowContent.isManuallySet = options.preserveFlags.isManuallySet ?? (options.isManuallySet ?? false);
  } else {
    // Set from options or defaults
    windowContent.isActive = options.isActive ?? false;
    windowContent.isManuallySet = options.isManuallySet ?? false;
    
    // Auto-detect isActive if this is the current active window
    if (!options.isActive && useSpaceStore.getState().activeWindowId === windowId) {
      windowContent.isActive = true;
    }
  }
  
  return windowContent;
};

// Debounced persistence helper to save context state to spaceStore
// ── localStorage backup for context state ────────────────────────────────────
// Survives tab close when the debounced spaceStore save hasn't flushed yet.
// Key: river_context_v1_{spaceId} | TTL: 24 hours
const CONTEXT_LS_KEY = (spaceId: string) => `river_context_v1_${spaceId}`;

const saveContextToLocalStorage = debounce((getState: () => ContextStore) => {
  try {
    const state = getState();
    const spaceId = state.context?.spaceId;
    if (!spaceId) return;
    const snapshot = {
      entityReferences: state.entityReferences.map(ref => ({
        llmId: ref.llmId,
        appType: ref.appType,
        entityType: ref.entityType,
        isActive: ref.isActive,
        documentRole: ref.documentRole,
      })),
      explicitContent: state.explicitContent.map(c => ({
        ...c,
        sourceDocument: c.sourceDocument
          ? { llmId: c.sourceDocument.llmId, nodeRange: c.sourceDocument.nodeRange }
          : undefined,
      })),
      savedAt: Date.now(),
    };
    localStorage.setItem(CONTEXT_LS_KEY(spaceId), JSON.stringify(snapshot));
  } catch {
    // localStorage unavailable (private mode quota exceeded etc.)
  }
}, 500);

const persistContextToSpaceStore = debounce(async (getState: () => ContextStore) => {
  // Always save to localStorage immediately (synchronous, survives tab close)
  saveContextToLocalStorage(getState);
  saveContextToLocalStorage.flush(); // ensure it fires now, not after debounce
  
  try {
    const state = getState();
    const spaceStore = useSpaceStore.getState();
    const activeSpace = spaceStore.activeSpace;
    
    if (!activeSpace) return;
    // Titles should always be looked up from entityStore on page load
    // Only persist identifiers (llmId, entityId) and structural data
    const contextState = {
      entityReferences: state.entityReferences.map(ref => ({
        llmId: ref.llmId,
        // title: ref.title,
        appType: ref.appType,
        entityType: ref.entityType,
        isActive: ref.isActive,
        windowId: ref.windowId,
        documentRole: ref.documentRole
      })),
      explicitContent: state.explicitContent.map(content => ({
        ...content,
        sourceDocument: content.sourceDocument ? {
          llmId: content.sourceDocument.llmId,
          // title: content.sourceDocument.title,
          nodeRange: content.sourceDocument.nodeRange
        } : undefined
      })),
      entityContexts: state.entityContexts.map(ctx => ({
        entityId: ctx.entityId,
        // name: ctx.name,
        entityType: ctx.entityType,
        llmId: ctx.llmId,
        content: ctx.content,
        metadata: ctx.metadata
      })),
      fileContexts: state.fileContexts,
      webSearchEnabled: state.webSearchEnabled
    };
    
    const newSettings = {
      ...activeSpace.settings,
      contextState
    };
    // Update spaceStore's activeSpace settings
    useSpaceStore.setState((state) => ({
      ...state,
      activeSpace: {
        ...activeSpace,
        settings: newSettings
      }
    }));
    
    // Trigger backend save via spaceStore
    await spaceStore.saveToBackend();
    
    log.log('[contextStore] Persisted context state to backend');
  } catch (error) {
    log.error('[contextStore] Failed to persist context state:', error);
  }
}, 500);

export const useContextStore = create<ContextStore>((set, get) => {
  return {
    context: {
      spaceId: '',
      entityRefs: {},
    },
    entityContexts: [],
    fileContexts: [],
    webSearchEnabled: true,  // Default to always on
    entityReferences: [],
    explicitContent: [],
    currentSubmit: null,
    _lastLocalModification: null,
    
    _markLocalModification: () => {
      set({ _lastLocalModification: Date.now() });
    },

    setActiveSpace: (spaceId: string) => {
      set((state) => ({ context: { ...state.context, spaceId } }));

      // Wire up Y.Doc chatContext sync for this space (cross-tab + headless access)
      // Delayed slightly so the Space Y.Doc has time to connect via crdtServiceWS
      setTimeout(() => initChatContextYDocSync(spaceId), 500);
      
      // Load saved context state from space settings
      const spaceStore = useSpaceStore.getState();
      const activeSpace = spaceStore.activeSpace;

      // ── localStorage fallback ────────────────────────────────────────────────
      // When the spaceStore hasn't loaded from backend yet, or when the backend save
      // hadn't flushed before the tab closed last time, restore from localStorage.
      // Only applies when spaceStore has no contextState for this space.
      if (!activeSpace?.settings?.contextState) {
        try {
          const localRaw = localStorage.getItem(CONTEXT_LS_KEY(spaceId));
          if (localRaw) {
            const localSnap = JSON.parse(localRaw) as { entityReferences?: any[]; explicitContent?: any[]; savedAt: number };
            const ageMs = Date.now() - (localSnap.savedAt || 0);
            // Only restore if saved within the last 24 hours
            const restoredRefs = localSnap.entityReferences || (localSnap as any).documentReferences || [];
            if (ageMs < 86_400_000 && (restoredRefs.length || localSnap.explicitContent?.length)) {
              set({
                entityReferences: restoredRefs,
                explicitContent: localSnap.explicitContent || [],
              });
              log.log('[contextStore] 📦 RESTORED context from localStorage:', {
                entityReferences: restoredRefs.length,
                explicitContent: localSnap.explicitContent?.length,
                ageSeconds: Math.round(ageMs / 1000),
              });
            }
          }
        } catch {
          // Malformed JSON or quota — ignore
        }
      }
      
      if (activeSpace?.settings?.contextState) {
        const { contextState } = activeSpace.settings;
        // Read from new key first; fall back to old key for spaces saved before the rename.
        const backendRefs = contextState.entityReferences || (contextState as any).documentReferences || [];
        const inMemoryRefs = get().entityReferences;
        const lastModified = get()._lastLocalModification;
        const hasRecentLocalChanges = lastModified && (Date.now() - lastModified < 10000); // 10 seconds
        // 1. If recent local changes: Keep in-memory currentDoc, merge backend contextDocs
        // 2. If no local changes: Safe to restore from backend (page reload, first load)
        
        let finalDocRefs: EntityReference[];
        
        if (hasRecentLocalChanges && inMemoryRefs.length > 0) {
          log.log('[contextStore] 🛡️ Protecting recent local changes from backend overwrite:', {
            inMemoryCount: inMemoryRefs.length,
            backendCount: backendRefs.length,
            lastModifiedAgo: Date.now() - lastModified!
          });
          
          // Keep the current active document from in-memory (most recent)
          const currentDoc = inMemoryRefs.find(ref => ref.isActive);
          
          // Get context documents from backend (user-added docs)
          const backendContextDocs = backendRefs.filter((ref: EntityReference) => 
            ref.documentRole === 'contextDoc' && 
            (!currentDoc || ref.llmId !== currentDoc.llmId) // Don't duplicate current doc
          );
          
          // Merge: current doc first, then backend context docs
          if (currentDoc) {
            finalDocRefs = [currentDoc, ...backendContextDocs];
            log.log('[contextStore] ✅ Smart merge: current doc preserved:', {
              currentDoc: currentDoc.llmId,
              addedContextDocs: backendContextDocs.length
            });
          } else {
            // No current doc in memory - use backend state
            finalDocRefs = backendRefs;
          }
        } else {
          finalDocRefs = backendRefs;
          log.log('[contextStore] 📦 RESTORED context from backend (no local conflicts):', {
            entityReferences: finalDocRefs.length,
            restoredRefs: finalDocRefs.map((r: any) => ({ llmId: r.llmId, title: r.title })),
            timestamp: new Date().toISOString()
          });
        }
        const entityStoreEntities = useEntityStore.getState().entities;
        
        const validatedRefs = finalDocRefs.filter((ref: EntityReference) => {
          const entityExists = entityStoreEntities.some((e: any) => 
            e.llmId === ref.llmId || e._id === ref.llmId
          );
          
          if (!entityExists) {
            log.log('[contextStore] 🗑️ Removing orphaned reference (no entity found):', {
              llmId: ref.llmId,
              title: ref.title
            });
            return false;
          }
          
          return true;
        });
        
        log.log('[contextStore] ✅ Validated document references:', {
          original: finalDocRefs.length,
          validated: validatedRefs.length,
          removed: finalDocRefs.length - validatedRefs.length
        });
        const hydratedRefs = validatedRefs.map((ref: EntityReference) => {
          const entity = entityStoreEntities.find((e: any) => 
            e.llmId === ref.llmId || e._id === ref.llmId
          );
          
          return {
            ...ref,
            title: entity?.name || ref.title || 'Untitled'  // Use fresh entity name
          };
        });
        const hydratedEntityContexts = (contextState.entityContexts || []).map((ctx: any) => {
          const entity = entityStoreEntities.find((e: any) => 
            e._id === ctx.entityId || e.llmId === ctx.llmId
          );
          
          return {
            ...ctx,
            name: entity?.name || ctx.name || 'Untitled'
          };
        });
        const hydratedExplicitContent = (contextState.explicitContent || []).map((content: any) => {
          if (content.sourceDocument?.llmId) {
            const entity = entityStoreEntities.find((e: any) => 
              e.llmId === content.sourceDocument.llmId
            );
            
            return {
              ...content,
              sourceDocument: {
                ...content.sourceDocument,
                title: entity?.name || content.sourceDocument.title || 'Untitled'
              }
            };
          }
          return content;
        });
        
        log.log('[contextStore] ✅ Hydrated titles from entityStore on load');
        
        // Apply the final state with hydrated titles
        set({
          entityReferences: hydratedRefs,
          explicitContent: hydratedExplicitContent,
          entityContexts: hydratedEntityContexts,
          fileContexts: contextState.fileContexts || [],
          webSearchEnabled: true  // Always enabled (ignore persisted value)
        });
      }
    },

    removeWindowContent: async (storageKey: string) => {
      // Since we store by llmId, this storageKey IS the llmId in most cases
      const currentState = get();
      const contentBeingRemoved = (currentState.context as any).entityRefs?.[storageKey];
      let llmId = (contentBeingRemoved as any)?.entityLlmId || 
                  (contentBeingRemoved as any)?.llmId;
      
      // If still no llmId, the storageKey itself might be the llmId (which is how we store items)
      if (!llmId && storageKey && (storageKey.startsWith('doc_') || storageKey.startsWith('note_') || storageKey.startsWith('prompt_'))) {
        llmId = storageKey;
      }
      
      log.log('[contextStore] 🗑️ Removing content by storage key:', {
        storageKey,
        extractedLlmId: llmId,
        contentKeys: contentBeingRemoved ? Object.keys(contentBeingRemoved) : null,
        contentType: typeof contentBeingRemoved,
        isLlmIdKey: storageKey.includes('_') // llmIds have underscores, windowIds have dashes
      });
      
      // Entity-first: do not maintain local entityRefs here; persist via spaceStore only
      if (llmId) {
        try {
          const spaceStoreModule = (await import('../../../../stores/spaceStore')).default;
          spaceStoreModule.getState().removeFromContext(llmId);
          log.log('[contextStore] ✅ Synced removal with spaceStore:', { storageKey, llmId });
        } catch (error) {
          console.warn('[contextStore] Failed to sync removal with spaceStore:', error);
        }
      } else {
        console.warn('[contextStore] ⚠️ No llmId found for removal, cannot sync with spaceStore:', {
          storageKey,
          contentBeingRemoved: contentBeingRemoved ? Object.keys(contentBeingRemoved) : null
        });
      }
    },

    setWindowContent: (windowId: string, update: Partial<WindowContent>) => {
      const currentContent = (get().context as any).entityRefs?.[windowId] || {};
      const newContent = {
        ...currentContent,
        ...update,
      } as WindowContent;

      set((state) => {
        return {
          context: {
            ...state.context,
            entityRefs: {
              ...((state.context as any).entityRefs || {}),
              [windowId]: newContent,
            },
          },
        };
      });
    },
    notifyAppReady: (windowId: string, metadata?: { appType?: string; contentLength?: number }) => {
      log.log(`[CONTEXT NOTIFICATION] App ready notification from ${windowId}:`, metadata);
      // Apps becoming ready should NOT flood the context with content
      // Context should only be populated when explicitly requested
      // Window content is no longer cached, so no refresh needed
      // The activeWindowId subscription handles all context updates
      
    },
    notifyContentUpdate: (entityIdentifier: string, metadata?: { contentLength?: number; source?: string }) => {
      log.log(`🔄 [TESTING] CONTENT_UPDATE_NOTIFICATION:`, {
        entityIdentifier,
        source: metadata?.source,
        contentLength: metadata?.contentLength,
        timestamp: new Date().toISOString(),
        phase: 'notification_received'
      });
      // Content updates should NOT flood the context with content
      // Context should only be populated when explicitly requested
      
      // 🔧 FIX: Check if entityIdentifier is in context (could be llmId or windowId)
      const entityRefs = (get().context as any).entityRefs || {};
      const currentContent = entityRefs[entityIdentifier];
      
      // Only refresh content if the entity is ALREADY in context
      if (currentContent) {
        log.log(`🔄 [TESTING] CONTEXT_REFRESH_TRIGGERED:`, {
          entityIdentifier,
          hadContent: !!currentContent.content,
          contentLength: currentContent.content?.length || 0,
          isInContext: true,
          refreshReason: metadata?.source || 'content_update',
          timestamp: new Date().toISOString(),
          phase: 'refresh_triggered'
        });
        
        // 🔧 FIX: If entityIdentifier is an llmId, we need to find the corresponding windowId
        let targetWindowId = entityIdentifier;
        
        // Check if this is an llmId (starts with doc_, note_, etc.) and find corresponding window
        if (entityIdentifier.includes('_')) {
          const { windows } = useSpaceStore.getState();
          const matchingWindow = Object.entries(windows).find(([winId]) => {
            const entity = useSpaceStore.getState().getWindowEntity(winId);
            return entity?.llmId === entityIdentifier;
          });
          
          if (matchingWindow) {
            targetWindowId = matchingWindow[0]; // Use the actual windowId
            log.log(`🔄 [TESTING] LLMID_TO_WINDOWID_MAPPED:`, {
              llmId: entityIdentifier,
              windowId: targetWindowId,
              timestamp: new Date().toISOString(),
              phase: 'key_mapping'
            });
          }
        }

        log.log(`✅ [TESTING] CONTEXT_REFRESH_COMPLETED:`, {
          entityIdentifier,
          targetWindowId,
          source: metadata?.source,
          timestamp: new Date().toISOString(),
          phase: 'refresh_completed',
          note: 'No refresh needed - activeWindowId subscription handles updates'
        });
      } else {
        log.log(`⚠️ [TESTING] CONTEXT_REFRESH_SKIPPED:`, {
          entityIdentifier,
          reason: 'not_in_context',
          availableEntityKeys: Object.keys(entityRefs),
          source: metadata?.source,
          timestamp: new Date().toISOString(),
          phase: 'refresh_skipped'
        });
      }
      
    },
    // This method is kept for backward compatibility but does nothing
    // Active window changes are handled automatically by _handleActiveWindowChange()
    addEntityToContext: async (entityId: string, _isManuallySet: boolean = false) => {
      log.log('[contextStore] ⚠️ DEPRECATED: addEntityToContext() called. Use contextStore.actions.addItemToContext() for manual additions.');
      log.log('[contextStore] Automatic context updates are now handled by _handleActiveWindowChange()');
      // No-op
    },
    // This method is kept for backward compatibility but does nothing
    // Active window changes are handled automatically by _handleActiveWindowChange()
    addItemToContext: async (itemId: string, _isManuallySet: boolean = false) => {
      log.log('[contextStore] ⚠️ DEPRECATED: addItemToContext() called. Use contextStore.actions.addItemToContext() for manual additions.');
      log.log('[contextStore] Automatic context updates are now handled by _handleActiveWindowChange()');
      // No-op - method kept for backward compatibility
    },

    // 🏗️ LEGACY: Keep for migration compatibility but redirect to reference-only system
    addDocExcerptForItem: async (itemId: string) => {
      log.log(`🏗️ [LEGACY_MIGRATION] addDocExcerptForItem called - redirecting to reference-only system:`, itemId);
      
      // Add to reference-only system instead
      try {
        const { entities } = useEntityStore.getState();
        const entity = entities.find((e: any) => e.id === itemId || e._id === itemId || e.llmId === itemId);
        
        if (entity && entity.llmId) {
          const entityReferences = get().entityReferences;
          const newRef: EntityReference = {
            llmId: entity.llmId,
            title: entity.name || 'Document',
            appType: entity.entityType === 'Doc' ? 'docs' : 'pdf',
            entityType: entity.entityType,
            isActive: false,
            addedAt: Date.now(),
            documentRole: 'contextDoc' // Legacy migration - treat as context document
          };
          
          const existingIndex = entityReferences.findIndex(ref => ref.llmId === entity.llmId);
          if (existingIndex >= 0) {
            const updatedRefs = [...entityReferences];
            updatedRefs[existingIndex] = newRef;
            set({ entityReferences: updatedRefs });
          } else {
            set({ entityReferences: [...entityReferences, newRef] });
          }
          
          log.log(`🏗️ [LEGACY_MIGRATION] Converted to document reference:`, {
            llmId: entity.llmId,
            title: entity.name,
            referenceOnly: true
          });
          
          return { added: true, llmId: entity.llmId, title: entity.name };
        }
      } catch (error) {
        console.error('[LEGACY_MIGRATION] Failed to convert to document reference:', error);
      }
      
      return { added: false };
    },
    
    // 🏗️ DEPRECATED: Legacy method for migration
    addDocExcerptForItemLegacy: async (itemId: string) => {
      try {
        // Resolve to entity and llmId
        let entity: any | null = null;
        let title: string | undefined;
        let llmId: string | undefined;

        const spaceState = useSpaceStore.getState();

        // If itemId is a window, resolve entity
        const maybeWindow = spaceState.windows[itemId];
        if (maybeWindow) {
          entity = spaceState.getWindowEntity(itemId);
        }

        // If not resolved, try entityStore by id or llmId
        if (!entity) {
          const { entities } = useEntityStore.getState();
          entity = entities.find((e: any) => 
            e.id === itemId || e._id === itemId || e.llmId === itemId
          ) || null;
        }

        if (!entity) {
          console.warn('[contextStore] addDocExcerptForItem: could not resolve entity for itemId', itemId);
          return { added: false };
        }

        // Determine if entity is a document
        const isDoc = (entity.entityType || '').toLowerCase() === 'doc' || (entity.entityType || '').toLowerCase() === 'document' || (getAppTypeByEntity(entity) || '').toLowerCase() === 'docs';
        if (!isDoc) {
          // Not a doc; do nothing here
          return { added: false };
        }

        llmId = entity.llmId || entity._id || entity.id;
        title = entity.name || 'Document';
        if (!llmId) {
          console.warn('[contextStore] addDocExcerptForItem: entity has no llmId', { itemId });
          return { added: false };
        }

        // Check if an excerpt for this document already exists
        const existing = get().explicitContent || [];
        const alreadyHasExcerpt = existing.some((exc: any) => exc?.sourceDocument?.llmId === llmId);
        if (alreadyHasExcerpt) {
          return { added: false, llmId, title };
        }

        // Add full-document excerpt using structured content (for explicit document additions)
        try {
          const { docReadV2Command } = await import('../../../../apps/DocsApp/commands/v5/doc_read');
          const result = await docReadV2Command.handler({ id: llmId, selector: 'all' } as any, {} as any);
          if (result.status === 'success' && result.data && Array.isArray((result.data as any).nodes)) {
            const nodes = (result.data as any).nodes;
            
            // Create excerpt if document has nodes (even if empty) - AI needs node structure
            const hasNodes = nodes.length > 0;
            const hasContent = nodes.some((node: any) => 
              node.text && node.text.trim().length > 0
            );
            
            log.log('[contextStore] 📊 Document excerpt analysis:', {
              llmId,
              nodeCount: nodes.length,
              hasNodes,
              hasContent,
              willCreateExcerpt: hasNodes,
              documentState: hasContent ? 'has_content' : 'empty_but_editable'
            });
            
            if (hasNodes) {
              const pseudoWindow: any = {
                id: `llm-${llmId}`,
                type: 'window',
                appType: 'docs',
                title: title || 'Document',
                entity: {
                  _id: llmId,
                  id: llmId,
                  name: title || 'Document',
                  entityType: 'Doc',
                  llmId
                }
              };
              // Enhanced excerpt with both legacy and new format for maximum compatibility
              const nodeIds = nodes.map((n: any) => n.id).filter((id: string) => id && id !== 'undefined');
              const markdownContent = (result.data as any).markdown || '';
              
              const excerpt: any = {
                text: markdownContent,
                structuredContent: {
                  ...result.data, // Legacy format
                  // Enhanced format for auto-excerpt compatibility
                  type: 'document_structure',
                  nodes: nodes,
                  availableNodeIds: nodeIds,
                  nodeCount: nodeIds.length,
                  markdown: markdownContent
                },
                source: {
                  window: pseudoWindow,
                  type: 'docs',
                  metadata: { 
                    llmId: llmId, 
                    hasStructuredContent: true, 
                    structuredContentSelector: 'all', 
                    extractionMethod: 'context_auto_excerpt',
                    nodeCount: nodeIds.length,
                    nodeIds: nodeIds.slice(0, 5), // Sample for debugging
                    hasContent,
                    fetchedAt: Date.now()
                  }
                }
              };
              await get().addTextExcerpt(excerpt);
              log.log('[contextStore] ✅ Created document excerpt with node structure:', {
                llmId,
                nodeCount: nodes.length,
                hasContent,
                excerptType: hasContent ? 'content_and_structure' : 'structure_only_for_editing',
                nodeIds: nodes.map((n: any) => n.id).slice(0, 3)
              });
              return { added: true, llmId, title };
            } else {
              log.log('[contextStore] ⚠️ Document has no nodes - cannot create excerpt:', llmId, {
                nodeCount: nodes.length,
                hasNodes: false,
                reason: 'no_editable_structure'
              });
              return { added: false, llmId, title };
            }
          }
        } catch (err) {
          try { console.warn('[contextStore] addDocExcerptForItem structured read failed', err); } catch {}
        }

        return { added: false, llmId, title };
      } catch (error) {
        try { console.warn('[contextStore] addDocExcerptForItem failed', error); } catch {}
        return { added: false };
      }
    },

    // Entity excerpts methods
    addTextExcerpt: async (excerpt: PastedContext) => {
    log.log('🔥 [ContextStore] addTextExcerpt called with:', formatJSON(excerpt, 'ADD_TEXT_EXCERPT'));
      // Clean the window object if excerpt exists
      if (excerpt && excerpt.source && excerpt.source.window) {
        const sourceWindow = excerpt.source.window;
        
        // Create minimal window object to avoid storing large fields
        const entity = sourceWindow.id ? useSpaceStore.getState().getWindowEntity(sourceWindow.id) : null;
        const entityNameFromMetadata = (excerpt.source.metadata as any)?.entityName;

        // Window and OpenDialog entities are transient — they get auto-trashed when the user
        // navigates away from a tab. When the window's current entity is one of these types,
        // try to find the underlying Doc entity by docId from the paste metadata instead.
        const isTransientWindowEntity = entity?.entityType === 'Window' || entity?.entityType === 'OpenDialog';
        const docIdFromMetadata = (excerpt.source.metadata as any)?.docId;
        let effectiveEntity = entity;
        if (isTransientWindowEntity && docIdFromMetadata) {
          const allEntities = useEntityStore.getState().entities;
          const docEntity = allEntities.find((e: any) => e._id === docIdFromMetadata || e.llmId === docIdFromMetadata);
          if (docEntity) {
            effectiveEntity = docEntity;
          }
        }

        const cleanedWindow: any = {
          id: sourceWindow.id || (sourceWindow as any)._id,
          type: sourceWindow.type || 'window',
          appType: sourceWindow.appType,
          title: sourceWindow.title || (sourceWindow as any).name || 'Untitled',
          entity: effectiveEntity ? {
            _id: (effectiveEntity as any)._id,
            id: (effectiveEntity as any).id,
            name: entityNameFromMetadata || (effectiveEntity as any).name,
            entityType: (effectiveEntity as any).entityType,
            llmId: (effectiveEntity as any).llmId
          } : undefined
        };
        if (cleanedWindow?.entity && !cleanedWindow.entity.llmId) {
          const { getWorkspaceEntities } = useSpaceStore.getState();
          const workspaceEntities = getWorkspaceEntities();
          
          // Try to find matching entity by llmId from metadata (fallback to docId for compatibility)
          const llmId = (excerpt.source.metadata as any)?.llmId || excerpt.source.metadata?.docId;
          if (llmId) {
            const matchingEntity = workspaceEntities.find(we => 
              we.entity?.llmId === llmId
            );
            
            if (matchingEntity?.entity?.llmId) {
              cleanedWindow.entity.llmId = matchingEntity.entity.llmId;
              log.log(`[contextStore] ✅ Populated missing llmId for entity excerpt: ${matchingEntity.entity.llmId}`);
            } else {
              log.warn(`[contextStore] ⚠️ Could not find llmId for entity excerpt with llmId: ${llmId}`);
            }
          }
        }
        // Handle explicit content for BOTH document selections AND plain text pastes
        if (cleanedWindow?.entity?.llmId) {
          // Document selection path
          const llmId = cleanedWindow.entity.llmId;

          if (excerpt.text && excerpt.text.trim()) {
            // Add immediately with raw text so the chip appears right away
            const immediateId = get().addExplicitContent({
              text: excerpt.text,
              sourceType: 'selection',
              sourceDocument: {
                llmId: llmId,
                title: cleanedWindow.entity?.name || cleanedWindow.title || 'Document',
                nodeRange: excerpt.source.metadata?.nodeRange ? {
                  startNode: excerpt.source.metadata.nodeRange.startNode,
                  endNode: excerpt.source.metadata.nodeRange.endNode,
                  isSameNode: excerpt.source.metadata.nodeRange.isSameNode
                } : undefined
              },
              clearAfterSubmit: true,
              metadata: {
                wordCount: excerpt.text.trim().split(/\s+/).length,
                originalLength: excerpt.text.length,
                hasStructuredContent: false,
                originalText: excerpt.text
              }
            });

            // Asynchronously enhance with structured content if we have node range info
            if (excerpt.source.type === 'docs' && excerpt.source.metadata?.nodeRange) {
              (async () => {
                try {
                  log.log(`[contextStore] 🔄 Enhancing entity excerpt with structured content for llmId: ${llmId}`);
                  const { docReadV2Command } = await import('../../../../apps/DocsApp/commands/v5/doc_read');

                  const nodeRange = excerpt.source.metadata!.nodeRange!;
                  let selector: any | null = null;

                  if (nodeRange.startNode && nodeRange.endNode) {
                    if (nodeRange.isSameNode) {
                      selector = { nodeIds: [nodeRange.startNode] };
                    } else {
                      selector = { range: { from: nodeRange.startNode, to: nodeRange.endNode } };
                    }
                  }

                  if (!selector) return;

                  const result: any = await docReadV2Command.handler(
                    { id: llmId, selector },
                    {} as any
                  );

                  if (result && result.status === 'success' && result.data) {
                    const totalNodes = (
                      (Array.isArray((result.data as any).nodes) && (result.data as any).nodes.length) ||
                      (result.data as any).metadata?.totalNodes ||
                      0
                    ) || 1;

                    let normalizedSelector = selector;
                    if (
                      totalNodes > 1 &&
                      selector && typeof selector === 'object' && Array.isArray((selector as any).nodeIds) &&
                      (selector as any).nodeIds.length > 0
                    ) {
                      const nodeIds: string[] = (selector as any).nodeIds as string[];
                      normalizedSelector = { range: { from: nodeIds[0], to: nodeIds[nodeIds.length - 1] } };
                    }

                    const structuralInfo = (result.data as any).structuralInfo || {};

                    log.log(`[contextStore] ✅ Structured content ready, updating chip (${totalNodes} nodes)`);
                    get().updateExplicitContent(immediateId, {
                      text: excerpt.text,
                      structuredContent: {
                        nodes: result.data.nodes || [],
                        availableNodeIds: result.data.availableNodeIds || [],
                        nodeCount: result.data.nodeCount,
                        selector: normalizedSelector
                      },
                      metadata: {
                        wordCount: excerpt.text.trim().split(/\s+/).length,
                        originalLength: excerpt.text.length,
                        hasStructuredContent: true,
                        originalText: excerpt.text,
                        structuredContentSelector: normalizedSelector,
                        originalRange: nodeRange.startNode && nodeRange.endNode ? {
                          from: nodeRange.startNode,
                          to: nodeRange.endNode,
                          isSameNode: nodeRange.isSameNode,
                          totalNodes
                        } : undefined,
                        structuralInfo,
                        formattingHints: structuralInfo.preservationHints || [],
                        hasComplexFormatting: structuralInfo.hasComplexLists || structuralInfo.hasNestedLists || false
                      }
                    });
                  } else {
                    log.warn(`[contextStore] ⚠️ Failed to get structured content:`, result?.message);
                  }
                } catch (error) {
                  log.warn(`[contextStore] ⚠️ Error enhancing excerpt with structured content:`, error);
                }
              })();
            }

            // Emit pastedContextChange for context highlighting plugin compatibility
            if (excerpt.source.type === 'docs' && excerpt.source.metadata?.docId) {
              contextEventEmitter.emit('pastedContextChange', excerpt);
            }
          } else {
            console.warn('⚠️ [ADD_EXPLICIT_CONTENT] Skipped - excerpt.text is empty!');
          }
        } else if (excerpt.source.type === 'paste' && excerpt.text && excerpt.text.trim()) {
          log.log('🎯 [PASTE_DEBUG] Adding paste to explicit content (cleanedWindow path)!');
          
          const wordCount = excerpt.source.metadata?.wordCount || excerpt.text.trim().split(/\s+/).length;
          const isLargePaste = wordCount > 500;
          // Small pastes can be cleared after submit (one-off context)
          get().addExplicitContent({
            text: excerpt.text,
            sourceType: 'paste',
            sourceDocument: undefined, // No source document for plain text pastes
            clearAfterSubmit: !isLargePaste, // Keep large pastes, clear small ones
            metadata: {
              wordCount,
              originalLength: excerpt.text.length
            }
          });
          
          log.log('🎯 [PASTE_DEBUG] ✅ Added plain text paste to explicit content:', {
            wordCount,
            isLargePaste,
            willPersist: isLargePaste
          });
        }
      } else {
        // No cleanedWindow path: Add to explicit content for plain text pastes
        log.log('🎯 [PASTE_DEBUG] Checking if should add to explicit content:', {
          sourceType: excerpt.source.type,
          hasText: !!excerpt.text,
          textLength: excerpt.text?.length || 0,
          textTrimmed: excerpt.text?.trim().length || 0
        });
        
        if (excerpt.source.type === 'paste' && excerpt.text && excerpt.text.trim()) {
          log.log('🎯 [PASTE_DEBUG] Adding paste to explicit content now!');
          
          const wordCount = excerpt.source.metadata?.wordCount || excerpt.text.trim().split(/\s+/).length;
          const isLargePaste = wordCount > 500;
          // Small pastes can be cleared after submit (one-off context)
          get().addExplicitContent({
            text: excerpt.text,
            sourceType: 'paste',
            sourceDocument: undefined, // No source document for plain text pastes
            clearAfterSubmit: !isLargePaste, // Keep large pastes, clear small ones
            metadata: {
              wordCount,
              originalLength: excerpt.text.length
            }
          });
          
          log.log('🎯 [PASTE_DEBUG] ✅ Added paste to explicit content:', {
            textLength: excerpt.text.length,
            wordCount,
            isLargePaste,
            willPersist: isLargePaste
          });
        } else {
          log.log('🎯 [PASTE_DEBUG] NOT adding to explicit content - conditions not met');
        }
      }
    },

    // Entity context methods
    addEntityContext: async (entityContext: EntityContext) => {
      // Check PDF processing status if this is a PDF entity
      const isPDF = entityContext.entityType === 'PDF' || entityContext.entityType?.includes('PDF');
      
      // Skip processing check for temporary/uploading states or unauthenticated users
      const isTemporary = entityContext.entityId?.startsWith('uploading-');
      const isUploading = (entityContext.metadata as any)?.isUploading;
      const hasAuth = !!localStorage.getItem('authToken');
      
      if (isPDF && !entityContext.metadata.isProcessed && !isTemporary && !isUploading && hasAuth) {
        // Asynchronously check processing status (only for authenticated users with real entities)
        try {
          const { pdfProcessingService } = await import('../../../../services/pdfProcessingService');
          const status = await pdfProcessingService.checkProcessingStatus(entityContext.entityId);
          
          // Update metadata with processing status
          entityContext.metadata.isProcessed = status.isProcessed;
          
          log.info('Checked PDF processing status:', {
            entityId: entityContext.entityId,
            name: entityContext.name,
            isProcessed: status.isProcessed,
            enrichmentStatus: status.enrichmentStatus
          });
          
          // Show notification if PDF is still processing
          if (!status.isProcessed || status.needsProcessing || status.enrichmentStatus === 'processing') {
            const processingMessage = status.enrichmentStatus === 'processing' 
              ? `"${entityContext.name}" is being processed...`
              : `"${entityContext.name}" is being processed. Please wait a moment before asking about it.`;
            
            console.info(`📄 ${processingMessage}`);
            
            // Show toast notification
            try {
              const { toastService } = await import('../../../../services/ui/toastService');
              toastService.info(processingMessage, { duration: 5000 });
            } catch (error) {
              // Toast system not available, that's okay
            }
          }
        } catch {
          log.warn('Failed to check PDF processing status');
          // Default to unprocessed on error
          entityContext.metadata.isProcessed = false;
        }
      }
      
      set((state) => {
        const existingIndex = state.entityContexts.findIndex(ctx => ctx.entityId === entityContext.entityId);
        let newEntityContexts;
        
        if (existingIndex !== -1) {
          // Update existing context
          newEntityContexts = [...state.entityContexts];
          newEntityContexts[existingIndex] = entityContext;
        } else {
          // Add new context, limit to 5 most recent
          newEntityContexts = [entityContext, ...state.entityContexts].slice(0, 5);
        }
        
        return { entityContexts: newEntityContexts };
      });
      
      log.info('Added entity context:', { 
        entityId: entityContext.entityId, 
        name: entityContext.name,
        isProcessed: entityContext.metadata.isProcessed 
      });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    removeEntityContext: (entityId: string) => {
      set((state) => ({
        entityContexts: state.entityContexts.filter(ctx => ctx.entityId !== entityId)
      }));
      
      log.info('Removed entity context:', { entityId });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    clearEntityContexts: () => {
      set(() => ({ entityContexts: [] }));
    },

    getEntityContexts: () => {
      return get().entityContexts;
    },

    hasEntityContext: (entityId: string) => {
      return get().entityContexts.some(ctx => ctx.entityId === entityId);
    },

    markPDFAsProcessed: (entityId: string) => {
      set((state) => ({
        entityContexts: state.entityContexts.map(ctx => {
          if (ctx.entityId === entityId && (ctx.entityType === 'PDF' || ctx.entityType?.includes('PDF'))) {
            return {
              ...ctx,
              metadata: {
                ...ctx.metadata,
                isProcessed: true
              }
            };
          }
          return ctx;
        })
      }));
      
      log.info('Marked PDF as processed:', { entityId });
      
      // Clear pdfProcessingService cache
      import('../../../../services/pdfProcessingService').then(({ pdfProcessingService }) => {
        pdfProcessingService.clearCache(entityId);
      }).catch(err => {
        log.warn('Failed to clear PDF processing cache:', err);
      });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    // File context methods
    addFileContext: (fileContext: FileContext) => {
      set((state) => {
        const existingIndex = state.fileContexts.findIndex(ctx => ctx.fileName === fileContext.fileName);
        let newFileContexts;
        
        if (existingIndex !== -1) {
          // Update existing context
          newFileContexts = [...state.fileContexts];
          newFileContexts[existingIndex] = fileContext;
        } else {
          // Add new context, limit to 5 most recent
          newFileContexts = [fileContext, ...state.fileContexts].slice(0, 5);
        }
        
        return { fileContexts: newFileContexts };
      });
      
      log.info('Added file context:', { fileName: fileContext.fileName, fileType: fileContext.fileType });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    removeFileContext: (fileName: string) => {
      set((state) => ({
        fileContexts: state.fileContexts.filter(ctx => ctx.fileName !== fileName)
      }));
      
      log.info('Removed file context:', { fileName });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    clearFileContexts: () => {
      set(() => ({ fileContexts: [] }));
    },

    getFileContexts: () => {
      return get().fileContexts;
    },

    hasFileContext: (fileName: string) => {
      return get().fileContexts.some(ctx => ctx.fileName === fileName);
    },

    // Upload state management methods (NEW) - Now using uploadId for reliable matching
    updateFileContextProgress: (uploadId: string, progress: number) => {
      set((state) => ({
        fileContexts: state.fileContexts.map(ctx =>
          ctx.metadata.uploadId === uploadId
            ? { ...ctx, metadata: { ...ctx.metadata, uploadProgress: progress } }
            : ctx
        )
      }));
    },

    updateFileContextComplete: (uploadId: string, entity: any) => {
      set((state) => ({
        fileContexts: state.fileContexts.map(ctx =>
          ctx.metadata.uploadId === uploadId
            ? {
                ...ctx,
                metadata: {
                  ...ctx.metadata,
                  isUploading: false,
                  entityId: entity._id || entity.id,  // ← Handle both _id and id (guest uploads use id)
                  llmId: entity.llmId,
                  uploadProgress: 100,
                  uploadError: undefined
                }
              }
            : ctx
        )
      }));
      
      // Find the file context to get fileName for logging
      const fileContext = get().fileContexts.find(ctx => ctx.metadata.uploadId === uploadId);
      log.info('File upload complete:', { 
        uploadId, 
        fileName: fileContext?.fileName, 
        entityId: entity._id, 
        llmId: entity.llmId 
      });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    updateFileContextError: (uploadId: string, error: string) => {
      set((state) => ({
        fileContexts: state.fileContexts.map(ctx =>
          ctx.metadata.uploadId === uploadId
            ? {
                ...ctx,
                metadata: {
                  ...ctx.metadata,
                  isUploading: false,
                  uploadError: error
                }
              }
            : ctx
        )
      }));
      
      // Find the file context to get fileName for logging
      const fileContext = get().fileContexts.find(ctx => ctx.metadata.uploadId === uploadId);
      log.error('File upload failed:', { uploadId, fileName: fileContext?.fileName, error });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    // NEW: Update fileContext with llmId after enrichment completes
    updateFileContextWithLlmId: (entityId: string, llmId: string) => {
      set((state) => ({
        fileContexts: state.fileContexts.map(ctx =>
          ctx.metadata.entityId === entityId && !ctx.metadata.llmId
            ? {
                ...ctx,
                metadata: {
                  ...ctx.metadata,
                  llmId: llmId
                }
              }
            : ctx
        )
      }));
      
      // Find the file context to get fileName for logging
      const fileContext = get().fileContexts.find(ctx => ctx.metadata.entityId === entityId);
      log.info('📝 File context updated with llmId after enrichment:', { 
        entityId, 
        llmId, 
        fileName: fileContext?.fileName 
      });
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    // Web search flag methods
    toggleWebSearch: () => {
      set((state) => {
        const newValue = !state.webSearchEnabled;
        log.log('[contextStore] 🌐 Web search toggled:', newValue);
        return { webSearchEnabled: newValue };
      });
    },

    setWebSearch: (enabled: boolean) => {
      set({ webSearchEnabled: enabled });
      log.log('[contextStore] 🌐 Web search set to:', enabled);
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },

    // Unified context clearing methods
    clearAllContext: () => {
      const state = get();
      
      // Optimization: Skip if context is already empty
      const hasContext = 
        state.entityContexts.length > 0 ||
        state.fileContexts.length > 0 ||
        state.explicitContent.length > 0 ||
        state.entityReferences.length > 0;
      
      if (!hasContext) {
        log.log('[contextStore.clearAllContext] Context already empty, skipping');
        return;
      }
      
      log.log('[contextStore.clearAllContext] 🗑️ Clearing all context:', {
        entityReferences: state.entityReferences.length,
        currentDocs: state.entityReferences.filter(r => r.documentRole === 'currentDoc').map(r => r.llmId),
        contextDocs: state.entityReferences.filter(r => r.documentRole === 'contextDoc').map(r => r.llmId),
        entityContexts: state.entityContexts.length,
        fileContexts: state.fileContexts.length,
        explicitContent: state.explicitContent.length
      });
      
      // Clear all context types
      state.clearEntityContexts();
      state.clearFileContexts();
      state.clearExplicitContent();  // Clear user excerpts (pastes, selections)
      
      // Clear document references (the new system)
      set((state) => ({
        entityReferences: [],
        webSearchEnabled: true,        // 🌐 Reset web search to default (always on)
        currentSubmit: null
      }));
      
      // Clear from spaceStore as well
      try {
        spaceStore.setState({ contextEntities: {} });
      } catch {}
    },



    // Get tool execution context (entity-first)
    getToolExecutionContext: (params: {
      userId: string;
      threadId: string; 
      thread: any;
      model: string;
    }) => {
      const state = get();
      const activeSpace = useSpaceStore.getState().activeSpace;
      const activeWindowId = useSpaceStore.getState().activeWindowId;
      
      // Build entity refs from current context
      const entityRefs = Object.fromEntries(
        Object.entries(((state.context as any).entityRefs) || {}).map(([key, info]) => [
          key,
          {
            ...(info || {}),
            isManuallySet: (info as any)?.isManuallySet === true
          }
        ])
      );
      log.log(formatJSON({
        entityContextsCount: state.entityContexts.length,
        entityContexts: state.entityContexts.map(ctx => ({ name: ctx.name, type: ctx.entityType })),
        phase: 'contextStore_getToolExecutionContext'
      }, '🚨 [CONTEXT_HOT_PATH] contextStore building context'));

      // Build context with all necessary info including website contexts
      const toolContext = {
        // Core identifiers
        userId: params.userId,
        threadId: params.threadId,
        spaceId: activeSpace?.id,
        sessionId: userTrackingService.getSessionId(),
        
        // Chat context (includes window contents, etc.)
        spaceContext: {
          ...state.context,
          entityRefs,
          // 🏗️ CRITICAL: Include all context types directly in spaceContext
          entityContexts: state.entityContexts,
          fileContexts: state.fileContexts,
          webSearchEnabled: state.webSearchEnabled // 🌐 Include web search flag
        },
        
        // 🏗️ CRITICAL: Include reference-only system properties for backend fresh content fetching
        entityReferences: state.entityReferences,
        contentFreshness: {
          backendMustFetchFresh: true,
          frontendSendsReferencesOnly: true,
          explicitContentWillBeClearedAfterSubmit: true
        },
        
        // Thread details
        threadMode: params.thread?.skeleton?.mode || 'chat',
        
        // Window and explicit content info
        activeWindowId: activeWindowId || undefined,
        explicitContent: state.explicitContent.length > 0 ? state.explicitContent : undefined,
        
        // 🏗️ UNIFIED: All context entities in a single array for AI
        availableEntities: (() => {
          const entities = [
            // Include document references
            ...state.entityReferences.map(ref => ({
              id: ref.llmId,
              type: 'document',
              name: ref.title,
              appType: ref.appType,
              entityType: ref.entityType,
              accessMethod: 'reference', // Backend fetches fresh content
              isActive: ref.isActive,
              role: ref.documentRole
            })),
          // Include entity contexts (dragged entities)
          ...state.entityContexts.map(ctx => ({
            id: ctx.llmId,
            type: 'entity',
            name: ctx.name,
            appType: ctx.entityType === 'File.Document.PDF' ? 'pdf' : ctx.entityType.toLowerCase(),
            entityType: ctx.entityType,
            accessMethod: 'entity', // Use entity-specific tools
            content: ctx.content,
            addedBy: ctx.metadata.addedBy
          })),
          // Include file contexts
          ...state.fileContexts.map(ctx => ({
            id: ctx.fileName,
            type: 'file',
            name: ctx.fileName,
            appType: 'file',
            entityType: 'File',
            accessMethod: 'file', // Use file tools
            fileSize: ctx.fileSize,
            mimeType: ctx.mimeType,
            addedBy: ctx.metadata.addedBy
          }))
        ];
        const docRefs = entities.filter(e => e.type === 'document');
        const activeDocRef = docRefs.find(e => 'isActive' in e && e.isActive && 'role' in e && e.role === 'currentDoc');
        
        log.log('[contextStore.getToolExecutionContext] 📤 SENDING CONTEXT TO AI:', {
          totalEntities: entities.length,
          entityReferences: docRefs.map(e => ({
            id: e.id,
            name: e.name,
            entityType: e.entityType,
            appType: e.appType,
            role: 'role' in e ? e.role : undefined,
            isActive: 'isActive' in e ? e.isActive : undefined
          })),
          entityContexts: entities.filter(e => e.type === 'entity').length,
          fileContexts: entities.filter(e => e.type === 'file').length,
          activeEntity: activeDocRef,
          allEntityTypes: [...new Set(entities.map(e => e.entityType).filter(Boolean))],
          allAppTypes: [...new Set(entities.map(e => e.appType).filter(Boolean))]
        });
        
        return entities;
      })(),
        
        // Legacy: Keep individual arrays for backward compatibility
        entityContexts: state.entityContexts.length > 0 ?
          state.entityContexts.map((ctx) => ({
            entityId: ctx.entityId,
            entityType: ctx.entityType,
            name: ctx.name,
            llmId: ctx.llmId,
            content: ctx.content,
            addedBy: ctx.metadata.addedBy,
            contentType: ctx.metadata.contentType
          })) : undefined,
        
        fileContexts: state.fileContexts.length > 0 ?
          state.fileContexts.map((ctx) => ({
            fileName: ctx.fileName,
            fileSize: ctx.fileSize,
            fileType: ctx.fileType,
            mimeType: ctx.mimeType,
            content: ctx.content,
            addedBy: ctx.metadata.addedBy,
            source: ctx.metadata.source
          })) : undefined,
        
        // Model settings
        model: params.model,
        
        // Timestamp for cache busting
        timestamp: Date.now(),
        
        // Context store reference (for tools that need it)
        contextStore: get,
        
        // 🌐 Web search flag (top-level for easy backend access)
        webSearchEnabled: state.webSearchEnabled
      };
      
      return toolContext;
    },

    // 🧠 NEW: Analyze document structure for semantic understanding
    analyzeDocumentStructure: (nodes: any[]) => {
      try {
        log.log(`🧠 [TESTING] DOCUMENT_STRUCTURE_ANALYSIS_START:`, {
          nodeCount: nodes?.length || 0,
          nodeTypes: nodes?.map(n => n.type).filter(Boolean) || [],
          timestamp: new Date().toISOString(),
          phase: 'structure_analysis_start'
        });
        
        const structure = {
          headingHierarchy: [] as Array<{level: number, text: string, nodeId: string, position: number}>,
          sectionTypes: new Set<string>(),
          logicalFlow: [] as Array<{nodeId: string, type: string, role: string}>,
          sectionMap: new Map<string, {start: number, end: number, type: string}>()
        };
        
        let currentSection: {nodeId: string, type: string, role: string} | null = null;
        
        nodes.forEach((node: any, index: number) => {
          const nodeType = node.type || 'unknown';
          const nodeText = node.text || '';
          const nodeId = node.id || `node_${index}`;
          
          structure.sectionTypes.add(nodeType);
          
          // Detect headings and build hierarchy
          if (nodeType.includes('heading') || nodeType === 'heading' || (node.attrs?.level && nodeType === 'heading')) {
            const level = node.attrs?.level || parseInt(nodeType.replace('heading', '')) || 1;
            structure.headingHierarchy.push({
              level,
              text: nodeText.substring(0, 50),
              nodeId,
              position: index
            });
            
            // Identify section roles
            const textLower = nodeText.toLowerCase();
            let role = 'content';
            if (textLower.includes('introduction') || textLower.includes('overview')) role = 'introduction';
            else if (textLower.includes('method') || textLower.includes('approach')) role = 'methods';
            else if (textLower.includes('result') || textLower.includes('finding')) role = 'results';
            else if (textLower.includes('discussion') || textLower.includes('analysis')) role = 'discussion';
            else if (textLower.includes('conclusion') || textLower.includes('summary')) role = 'conclusion';
            
            currentSection = { nodeId, type: nodeType, role };
          }
          
          // Map logical flow
          structure.logicalFlow.push({
            nodeId,
            type: nodeType,
            role: currentSection?.role || 'content'
          });
        });
        
        const result = {
          headingHierarchy: structure.headingHierarchy,
          sectionTypes: Array.from(structure.sectionTypes),
          logicalFlow: structure.logicalFlow,
          sectionMap: Object.fromEntries(structure.sectionMap),
          // Semantic insights for Claude
          hasIntroduction: structure.logicalFlow.some(n => n.role === 'introduction'),
          hasMethods: structure.logicalFlow.some(n => n.role === 'methods'),
          hasResults: structure.logicalFlow.some(n => n.role === 'results'),
          hasConclusion: structure.logicalFlow.some(n => n.role === 'conclusion'),
          documentFlow: structure.headingHierarchy.map(h => `${h.level}. ${h.text}`).join(' → ')
        };
        
        log.log(`🧠 [TESTING] DOCUMENT_STRUCTURE_ANALYSIS_COMPLETE:`, {
          nodeCount: nodes?.length || 0,
          headingCount: result.headingHierarchy.length,
          sectionTypeCount: result.sectionTypes.length,
          hasIntroduction: result.hasIntroduction,
          hasMethods: result.hasMethods,
          hasResults: result.hasResults,
          hasConclusion: result.hasConclusion,
          documentFlow: result.documentFlow,
          timestamp: new Date().toISOString(),
          phase: 'structure_analysis_complete'
        });
        
        return result;
      } catch (error) {
        console.error(`❌ [TESTING] DOCUMENT_STRUCTURE_ANALYSIS_FAILED:`, {
          nodeCount: nodes?.length || 0,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
          phase: 'structure_analysis_failed'
        });
        return { headingHierarchy: [], sectionTypes: [], logicalFlow: [], sectionMap: {} };
      }
    },



    dispose: () => {
      contextEventEmitter.removeAllListeners();
    },

    /**
     * ✅ NEW: Initialize reactive bindings to core stores
     * Subscribe to spaceStore changes to keep context in sync
     */
    initBindings: () => {
      const log = console.log; // Use console for now
      log('[contextStore] Initializing reactive bindings to core stores');
      
      let lastActiveWindowId: string | null = null;
      
      // Subscribe to spaceStore changes
      useSpaceStore.subscribe((state) => {
        const activeWindowId = state.activeWindowId;
        
        // Only react if activeWindowId actually changed
        if (activeWindowId === lastActiveWindowId) {
          return;
        }
        
        lastActiveWindowId = activeWindowId;
        
        if (!activeWindowId) {
          log('[contextStore] Active window cleared');
          return;
        }
        
        log('[contextStore] Active window changed:', activeWindowId);
        
        // Get the window entity
        const windowEntity = useSpaceStore.getState().getWindowEntity(activeWindowId);
        if (!windowEntity?.llmId) {
          log('[contextStore] Active window has no entity or llmId');
          return;
        }
        
        // Update document references to mark this as active
        const refs = get().entityReferences;
        const existingRef = refs.find(r => r.llmId === windowEntity.llmId);
        
        if (existingRef) {
          // Mark as active and update role to currentDoc
          get().addEntityReference(windowEntity.llmId, {
            ...existingRef,
            isActive: true,
            documentRole: 'currentDoc'
          });
        } else {
          // Add new reference as current doc
          const appType = windowEntity.entityType === 'Doc' ? 'docs' : (windowEntity.entityType === 'Note' ? 'notes' : 'pdf');
          get().addEntityReference(windowEntity.llmId, {
            title: windowEntity.name || 'Document',
            appType: appType as 'docs' | 'notes' | 'pdf',
            entityType: windowEntity.entityType,
            isActive: true,
            windowId: activeWindowId,
            documentRole: 'currentDoc'
          });
        }
        
        log('[contextStore] Updated context for active window:', {
          windowId: activeWindowId,
          llmId: windowEntity.llmId,
          name: windowEntity.name
        });
      });
      
      // Store unsubscribe functions for cleanup
      // TODO: Add cleanup mechanism when contextStore is disposed
      log('[contextStore] ✅ Reactive bindings initialized');
    },

    setContext: (newContext: SpaceContext) => {
      // Just set the context - entity excerpts are managed separately
      set({ context: newContext });
    },

    // New method to directly set active window and get fresh context
    setActiveWindowAndGetContext: async (windowId: string) => {
      const { windows } = useSpaceStore.getState();
      const window = windows[windowId];
      
      if (window) {
        const windowContent = await createWindowContent(window, { isManuallySet: false, isActive: true });
        if (windowContent) {
          
          // Entity-first: do not mutate windowContents here
          set({ context: { ...get().context } });
          return get().context;
        }
      }
      return get().context;
    },

    // Context refresh methods
    refreshWindowContent: async (windowId: string) => {
      log.log(`🔄 [TESTING] REFRESH_WINDOW_CONTENT_START:`, {
        windowId,
        timestamp: new Date().toISOString(),
        phase: 'refresh_start'
      });
      
      const { windows } = useSpaceStore.getState();
      const window = windows[windowId];
      
      if (!window) {
        console.error(`❌ [TESTING] REFRESH_FAILED_NO_WINDOW:`, {
          windowId,
          availableWindows: Object.keys(windows),
          timestamp: new Date().toISOString(),
          phase: 'refresh_failed'
        });
        return;
      }
      
      // Get entity from normalized storage
      const entity = useSpaceStore.getState().getWindowEntity(windowId);
      if (!entity || !entity.llmId) {
        console.error(`❌ [TESTING] REFRESH_FAILED_NO_ENTITY:`, {
          windowId,
          hasEntity: !!entity,
          hasLlmId: !!entity?.llmId,
          entityType: entity?.entityType,
          timestamp: new Date().toISOString(),
          phase: 'refresh_failed'
        });
        return;
      }
      
      const llmId = entity.llmId;
      const currentContent = (get().context as any).entityRefs?.[llmId];
      if (!currentContent) {
        console.warn(`⚠️ [TESTING] REFRESH_SKIPPED_NOT_IN_CONTEXT:`, {
          windowId,
          llmId,
          entityRefsKeys: Object.keys((get().context as any).entityRefs || {}),
          timestamp: new Date().toISOString(),
          phase: 'refresh_skipped'
        });
        return;
      }
      
      log.log(`🔄 [TESTING] REFRESH_PROCESSING:`, {
        windowId,
        llmId,
        appType: window.appType,
        hadContent: !!currentContent.content,
        oldContentLength: currentContent.content?.length || 0,
        timestamp: new Date().toISOString(),
        phase: 'refresh_processing'
      });
      
      // Create fresh window content by re-reading from the window
      const freshContent = await createWindowContent(window, {
        preserveFlags: {
          isActive: currentContent.isActive,
          isManuallySet: currentContent.isManuallySet
        }
      });
      
      if (freshContent) {
        log.log(`✅ [TESTING] REFRESH_SUCCESS:`, {
          windowId,
          llmId,
          appType: window.appType,
          contentChanged: currentContent.content !== freshContent.content,
          oldContentLength: currentContent.content?.length || 0,
          newContentLength: freshContent.content?.length || 0,
          oldContentPreview: currentContent.content?.substring(0, 100),
          newContentPreview: freshContent.content?.substring(0, 100),
          timestamp: new Date().toISOString(),
          phase: 'refresh_success',
          freshnessVerified: true
        });
        
        // Update the context with fresh content (use llmId as key for consistency)
        // Entity-first: do not store content locally
      } else {
        console.error(`❌ [TESTING] REFRESH_FAILED_NO_FRESH_CONTENT:`, {
          windowId,
          llmId,
          reason: 'createEnhancedWindowContent_returned_null',
          timestamp: new Date().toISOString(),
          phase: 'refresh_failed'
        });
      }
    },
    
    // Deprecated: entity-first flow removes window content refreshes
    refreshExistingWindowContents: () => get().context,

    // Deprecated: entity-first flow removes window content refreshes
    refreshAllWindowContents: () => get().context,

    // Token management (replaces ContextPriorityManager)
    optimizeContextForTokens: (maxTokens: number = 30000) => {
      const state = get();
      const context = state.context;
      
      // Simple token estimation (4 chars ≈ 1 token)
      const estimateTokens = (text: string) => Math.round(text.length / 4);
      
      // Collect all content with priorities
      interface ContentItem {
        id: string;
        content: string;
        priority: 'highest' | 'medium' | 'lowest';
        type: 'excerpt' | 'active-window' | 'context-window';
        metadata: any;
        tokenEstimate: number;
      }
      
      const contentItems: ContentItem[] = [];
      
      // 1. HIGHEST PRIORITY: Explicit content (user selections/pastes)
      state.explicitContent.forEach((item, index) => {
        contentItems.push({
          id: `explicit-${index}`,
          content: item.text || '',
          priority: 'highest',
          type: 'excerpt',
          metadata: item,
          tokenEstimate: estimateTokens(item.text || '')
        });
      });
      
      // 2. MEDIUM/LOWEST PRIORITY: Window contents
      Object.entries(((context as any).entityRefs) || {}).forEach(([windowId, windowContent]) => {
        const wc: any = windowContent as any;
        const content = extractWindowText(wc);
        contentItems.push({
          id: windowId,
          content: content,
          priority: wc?.isActive ? 'medium' : 'lowest',
          type: wc?.isActive ? 'active-window' : 'context-window',
          metadata: wc,
          tokenEstimate: estimateTokens(content)
        });
      });
      
      // Apply token budget
      let usedTokens = 0;
      const optimizedWindowContents: Record<string, WindowContent> = {};
      
      // Always include highest priority (excerpts are already in state)
      const excerptTokens = contentItems
        .filter(item => item.priority === 'highest')
        .reduce((sum, item) => sum + item.tokenEstimate, 0);
      usedTokens += excerptTokens;
      
      // Include medium priority up to budget
      const mediumItems = contentItems.filter(item => item.priority === 'medium');
      for (const item of mediumItems) {
        if (usedTokens + item.tokenEstimate <= maxTokens * 0.7) { // Reserve 30% for other content
          optimizedWindowContents[item.id] = item.metadata;
          usedTokens += item.tokenEstimate;
        }
      }
      
      // Include lowest priority with remaining budget
      const lowestItems = contentItems.filter(item => item.priority === 'lowest');
      for (const item of lowestItems) {
        if (usedTokens + item.tokenEstimate <= maxTokens) {
          optimizedWindowContents[item.id] = item.metadata;
          usedTokens += item.tokenEstimate;
        }
      }
      
      // Create a summary of all available IDs for AI guidance
      const availableIds = {
        documentIds: [] as string[],
        windowIds: [] as string[],
        activeDocumentId: null as string | null,
        activeWindowId: useSpaceStore.getState().activeWindowId || null
      };
      
      // Extract document IDs and window IDs from all available windows
      Object.entries(optimizedWindowContents).forEach(([windowId, content]) => {
        availableIds.windowIds.push(windowId);
        if ((content as any).documentId) {
          availableIds.documentIds.push((content as any).documentId);
          if ((content as any).isActive) {
            availableIds.activeDocumentId = (content as any).documentId;
          }
        }
      });
      
      // Return optimized context - MUST include explicit content
      return {
        ...context,
        entityRefs: optimizedWindowContents,
        explicitContent: state.explicitContent, // Always include explicit content (highest priority)
        availableIds // Add the ID summary for AI guidance
      };
    },
    
    // 🏗️ NEW: Build reference-only context for backend
    buildReferenceOnlyContext: (): ReferenceOnlyContext => {
      const state = get();
      return {
        spaceId: state.context.spaceId,
        entityReferences: state.entityReferences,
        explicitContent: state.explicitContent
      };
    },
    
    // 🏗️ NEW: Document reference management
    addEntityReference: (llmId: string, metadata: Partial<EntityReference>) => {
      const entityReferences = get().entityReferences;
      const existingIndex = entityReferences.findIndex(ref => ref.llmId === llmId);
      
      const newReference: EntityReference = {
        llmId,
        title: metadata.title || 'Document',
        appType: (metadata.appType === 'docs' || metadata.appType === 'pdf' || metadata.appType === 'notes') ? metadata.appType : 'docs',
        entityType: metadata.entityType,
        isActive: metadata.isActive || false,
        windowId: undefined,
        addedAt: metadata.addedAt || Date.now(),
        documentRole: (metadata as any).documentRole || 'contextDoc' // Default to contextDoc for user additions
      };

      let updatedRefs: EntityReference[];
      
      if (newReference.isActive && newReference.documentRole === 'currentDoc') {
        // When setting a new active currentDoc, remove all OTHER currentDoc entries and add/update
        // this one. contextDoc entries (user-manually added) are preserved. If this exact llmId is
        // already in the array (e.g. as a contextDoc being promoted), update it in place.
        if (existingIndex >= 0) {
          updatedRefs = entityReferences
            .filter(ref => ref.llmId === llmId || ref.documentRole !== 'currentDoc')
            .map(ref => ref.llmId === llmId ? newReference : ref);
        } else {
          updatedRefs = entityReferences.filter(ref => ref.documentRole !== 'currentDoc');
          updatedRefs.push(newReference);
        }
        
        log.log('[contextStore] 🏗️ Set new active currentDoc (replaced previous currentDocs):', {
          newActiveDoc: llmId,
          previousCurrentDocCount: entityReferences.filter(r => r.documentRole === 'currentDoc').length,
          title: newReference.title
        });
      } else if (newReference.isActive) {
        // Non-currentDoc active ref: clear isActive from others but don't remove them
        updatedRefs = entityReferences.map(ref => ({
          ...ref,
          isActive: false
        }));
        
        if (existingIndex >= 0) {
          updatedRefs[existingIndex] = newReference;
        } else {
          updatedRefs.push(newReference);
        }
        
        log.log('[contextStore] 🏗️ Set new active document (cleared isActive on others):', {
          newActiveDoc: llmId,
          title: newReference.title
        });
      } else {
        // Not setting as active - normal add/update
        if (existingIndex >= 0) {
          updatedRefs = [...entityReferences];
          updatedRefs[existingIndex] = newReference;
        } else {
          updatedRefs = [...entityReferences, newReference];
        }
      }
      
      set({ entityReferences: updatedRefs });
      get()._markLocalModification();
      
      log.log('[contextStore] 🏗️ Added/updated document reference:', {
        llmId,
        title: newReference.title,
        isActive: newReference.isActive,
        documentRole: newReference.documentRole,
        referenceOnly: true
      });
      
      // Persist to backend (debounced)
      persistContextToSpaceStore(get);
    },
    
    removeEntityReference: (llmId: string, options?: { force?: boolean; reason?: string }) => {
      // 🏗️ REFACTOR: Use internal helper (single mutation funnel)
      get()._applyRemoveEntityReference(llmId, {
        reason: options?.reason || 'manual_removal'
      });
    },
    addCurrentDocumentToContext: () => {
      const spaceStore = useSpaceStore.getState();
      const currentActiveWindowId = spaceStore.activeWindowId;
      
      if (!currentActiveWindowId) {
        log.log('[contextStore] 🔍 No active window - cannot add current document to context');
        return;
      }
      
      const activeEntity = spaceStore.getWindowEntity(currentActiveWindowId);
      if (!activeEntity || !activeEntity.llmId || activeEntity.entityType !== 'Doc') {
        log.log('[contextStore] 🔍 Active window has no valid document entity - skipping context addition:', {
          hasEntity: !!activeEntity,
          hasLlmId: activeEntity?.llmId,
          entityType: activeEntity?.entityType,
          windowId: currentActiveWindowId
        });
        return;
      }
      const entityStoreEntity = useEntityStore.getState().entities.find((e: any) => 
        e._id === activeEntity._id || e.llmId === activeEntity.llmId
      );
      
      if (!entityStoreEntity) {
        console.warn('[contextStore] ⚠️ Entity not found in EntityStore - may not be synced yet:', {
          llmId: activeEntity.llmId,
          entityId: activeEntity._id,
          title: activeEntity.name
        });
        // Continue anyway - entity might sync later
      } else {
        log.log('[contextStore] ✅ Entity validated in EntityStore:', {
          llmId: activeEntity.llmId,
          hasMetadata: 'nodeCount' in (entityStoreEntity as any),
          nodeCount: (entityStoreEntity as any).nodeCount,
          wordCount: (entityStoreEntity as any).wordCount
        });
      }
      
      // Check if already current doc
      const existingRef = get().entityReferences.find(r => r.llmId === activeEntity.llmId);
      if (existingRef && existingRef.isActive) {
        log.log('[contextStore] ✅ Document already current:', {
          llmId: activeEntity.llmId,
          title: activeEntity.name
        });
        return;
      }
      // Then add it back as currentDoc to ensure clean state
      if (existingRef && existingRef.documentRole === 'contextDoc') {
        get().removeEntityReference(activeEntity.llmId, { reason: 'promoting_to_current' });
        log.log('[contextStore] 🔄 Removed old context reference, will re-add as current');
      }
      
      // Remove the old current document (there should only be one)
      const oldCurrentDoc = get().entityReferences.find(r => r.isActive);
      if (oldCurrentDoc && oldCurrentDoc.llmId !== activeEntity.llmId) {
        get().removeEntityReference(oldCurrentDoc.llmId, { reason: 'current_doc_replacement' });
        log.log('[contextStore] 🔄 Removed old current document:', oldCurrentDoc.llmId);
      }
      
      // Add new current document
      get().addEntityReference(activeEntity.llmId, {
        title: activeEntity.name || 'Current Document',
        appType: 'docs',
        entityType: activeEntity.entityType,
        isActive: true,
        windowId: currentActiveWindowId,
        documentRole: 'currentDoc'
      });
      
      log.log('[contextStore] ✅ Added current document to context:', {
        llmId: activeEntity.llmId,
        title: activeEntity.name,
        windowId: currentActiveWindowId,
        reason: 'current_document_auto_add'
      });
    },
    
    getEntityReferences: () => get().entityReferences,
    
    hasEntityReference: (llmId: string) => {
      return get().entityReferences.some(ref => ref.llmId === llmId);
    },
    
    // 🏗️ NEW: Explicit content management
    addExplicitContent: (content: Omit<ExplicitContent, 'id' | 'addedAt'>): string => {
      const id = `explicit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newContent: ExplicitContent = {
        ...content,
        id,
        addedAt: Date.now(),
        clearAfterSubmit: content.clearAfterSubmit !== false // Default true
      };
      
      set({ explicitContent: [...get().explicitContent, newContent] });
      
      log.log('🎯 [PASTE_DEBUG] Added explicit content to store:', {
        id,
        sourceType: content.sourceType,
        textLength: content.text.length,
        textPreview: content.text.substring(0, 100),
        willClearAfterSubmit: newContent.clearAfterSubmit,
        currentExplicitContentCount: get().explicitContent.length,
        sourceDocument: content.sourceDocument
      });
      
      // Persist to backend
      persistContextToSpaceStore(get);
      
      return id;
    },
    
    updateExplicitContent: (id: string, updates: Partial<Omit<ExplicitContent, 'id' | 'addedAt'>>) => {
      const currentContent = get().explicitContent;
      const updated = currentContent.map(item =>
        item.id === id ? { ...item, ...updates } : item
      );
      set({ explicitContent: updated });
      persistContextToSpaceStore(get);
    },

    removeExplicitContent: (id: string) => {
      const currentContent = get().explicitContent;
      const itemToRemove = currentContent.find(item => item.id === id);
      const filteredContent = currentContent.filter(item => item.id !== id);
      set({ explicitContent: filteredContent });
      
      log.log('[contextStore] 🏗️ Removed explicit content:', id);
      
      // 🏗️ NEW: Clear highlights in document if this was from a document selection
      if (itemToRemove?.sourceDocument?.llmId) {
        try {
          const docId = itemToRemove.sourceDocument.llmId;
          const clearPayload = { text: '', source: { metadata: { docId } } };
          
          // Notify listeners using the same channel used for adding highlights
          contextEventEmitter.emit('pastedContextChange', clearPayload);
          
          // Fallback for plugins listening on the DOM event
          if (typeof document !== 'undefined') {
            try {
              const evt = new CustomEvent('contextHighlightUpdate', { detail: clearPayload });
              document.dispatchEvent(evt);
            } catch {}
          }
          
          log.log('[contextStore] 🎨 Cleared document highlights for explicit content removal:', {
            id,
            docId,
            sourceType: itemToRemove.sourceType
          });
        } catch (error) {
          console.warn('[contextStore] ⚠️ Failed to clear highlights for explicit content:', error);
        }
      }
      
      // Persist to backend
      persistContextToSpaceStore(get);
    },
    
    clearExplicitContent: () => {
      const itemsToRemove = get().explicitContent;
      
      // Optimization: Skip if already empty
      if (itemsToRemove.length === 0) {
        return;
      }
      
      set({ explicitContent: [] });
    },
    
    getExplicitContent: () => get().explicitContent,
    
    // 🏗️ NEW: Submit lifecycle management
    onSubmitStarting: () => {
      const submitId = `submit_${Date.now()}`;
      const state = get();
      set({ currentSubmit: {
        submitId,
        startedAt: Date.now(),
        entityRefs: [...state.entityReferences],
        explicitContent: [...state.explicitContent],
        status: 'pending'
      }});
      
      log.log('[contextStore] 🏗️ Submit started:', submitId);
    },
    
    onSubmitComplete: () => {
      const currentSubmit = get().currentSubmit;
      if (currentSubmit) {
        const clearedAt = Date.now();
        
        // Clear explicit content that has clearAfterSubmit flag
        const currentExplicitContent = get().explicitContent;
        const itemsToKeep = currentExplicitContent.filter(item => !item.clearAfterSubmit);
        const itemsCleared = currentExplicitContent.filter(item => item.clearAfterSubmit);
        
        set({ 
          currentSubmit: {
            ...currentSubmit,
            status: 'success',
            clearedAt
          },
          explicitContent: itemsToKeep
          // webSearchEnabled stays as-is (defaults to true, persists across messages)
        });
        
        // Auto-clear pasted content after submit.
        // Pasted content is added with clearAfterSubmit: true, so it gets cleared here.
        // Content can persist if clearAfterSubmit is explicitly set to false.
        
        log.log(`[contextStore] 🏗️ Submit completed: submitId=${currentSubmit.submitId} duration=${clearedAt - currentSubmit.startedAt}ms cleared=${itemsCleared.length} kept=${itemsToKeep.length}`);
        
        // Clear the current submit after a delay
        setTimeout(() => {
          if (get().currentSubmit?.submitId === currentSubmit.submitId) {
            set({ currentSubmit: null });
          }
        }, 1000);
      }
    },
    
    onSubmitFailed: (error?: string) => {
      const currentSubmit = get().currentSubmit;
      if (currentSubmit) {
        set({ currentSubmit: {
          ...currentSubmit,
          status: 'failed'
        }});
        
        log.log('[contextStore] 🏗️ Submit failed:', {
          submitId: currentSubmit.submitId,
          error,
          duration: Date.now() - currentSubmit.startedAt
        });
        
        // Clear the current submit after a delay
        setTimeout(() => {
          if (get().currentSubmit?.submitId === currentSubmit.submitId) {
            set({ currentSubmit: null });
          }
        }, 5000);
      }
    },
    
    // Get active rules based on current context
    getActiveRules: async () => {
      try {
        const useEntityStore = (await import('../../../../stores/entityStore')).default;
        const entityStore = useEntityStore.getState();
        let allRules = entityStore.entities.filter((e: any) => e.entityType === 'Rule');

        // Fall back to a direct API fetch only when the EntityStore cache is genuinely cold
        // (zero entities of ANY type → EntityStore hasn't loaded yet).
        // If EntityStore is populated but this user simply has no rules, skip the fetch —
        // doing it unconditionally would hit the API on every chat submit for ruleless users.
        const cacheIsWarm = entityStore.entities.length > 0;
        if (allRules.length === 0 && !cacheIsWarm) {
          try {
            const { entityService } = await import('../../../../services/entityService');
            allRules = await entityService.getEntitiesByType({ entityType: 'Rule' });
            log.log('[contextStore] EntityStore cache cold — fetched Rules from API:', {
              totalRules: allRules.length,
            });
          } catch (fetchErr) {
            console.warn('[contextStore] API fallback for Rules failed:', fetchErr);
          }
        } else {
          log.log('[contextStore] Reading Rules from EntityStore cache:', {
            totalRules: allRules.length,
            cacheIsWarm,
          });
        }
        
        const currentSpaceId = useSpaceStore.getState().currentSpaceId;
        const entityReferences = get().entityReferences;
        const activeDocument = entityReferences.find(ref => ref.isActive);
        
        const applicableRules = allRules.filter((rule: any) => {
          if (rule.trashed) return false;
          if (!rule.skeleton?.enabled) return false;
          
          const scope = rule.skeleton.scope || 'user';
          if (scope === 'user') return true;
          if (scope === 'space' && rule.skeleton.scopeId === currentSpaceId) return true;
          if (scope === 'document' && activeDocument && rule.skeleton.scopeId === activeDocument.llmId) return true;
          
          return false;
        });
        
        return applicableRules.map((rule: any) => ({
          id: rule._id,
          name: rule.name,
          instruction: rule.skeleton.instruction || '',
          scope: rule.skeleton.scope || 'user',
          scopeId: rule.skeleton.scopeId,
          scopeName: rule.skeleton.scopeName
        }));
      } catch (error) {
        console.error('[contextStore] Failed to get active rules:', error);
        return [];
      }
    },
    
    // ====== INTERNAL HELPERS (Single Mutation Funnel) ======
    // Following entityStore pattern - all mutations go through these helpers
    
    _applyAddEntityReference: (llmId: string, options) => {
      const { 
        title, appType, entityType, windowId, documentRole, isActive,
        currentSlide, totalSlides
      } = options as any;
      
      log.log('[contextStore._applyAddEntityReference] Adding document reference:', {
        llmId,
        title,
        documentRole,
        currentSlide,
        totalSlides
      });
      
      // Check duplicates
      const existing = get().entityReferences.find(ref => ref.llmId === llmId);
      if (existing) {
        log.log('[contextStore._applyAddEntityReference] Duplicate detected, skipping:', llmId);
        return;
      }
      
      // Create reference object with optional slide info
      const newRef: EntityReference = {
        llmId,
        title,
        appType,
        entityType,
        isActive,
        windowId,
        documentRole,
        addedAt: Date.now(),
        ...(currentSlide !== undefined && totalSlides !== undefined ? { currentSlide, totalSlides } : {})
      };
      
      // Update state
      set((state) => ({
        entityReferences: [...state.entityReferences, newRef]
      }));
      
      // Mark local modification
      get()._markLocalModification();
      
      // Persist to backend (debounced)
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyAddEntityReference] ✅ Document reference added', 
        currentSlide ? `with slide info (${currentSlide}/${totalSlides})` : ''
      );
    },
    
    _applyRemoveEntityReference: (llmId: string, options) => {
      const { reason } = options;
      
      log.log('[contextStore._applyRemoveEntityReference] Removing document reference:', {
        llmId,
        reason
      });
      
      // Remove from state
      set((state) => ({
        entityReferences: state.entityReferences.filter(ref => ref.llmId !== llmId)
      }));
      
      // Clear highlights if no more explicit content from this document
      const explicitContent = get().explicitContent;
      const hasExplicitContentFromDoc = explicitContent.some(item => 
        item.sourceDocument?.llmId === llmId
      );
      
      if (!hasExplicitContentFromDoc) {
        try {
          const clearPayload = { text: '', source: { metadata: { docId: llmId } } };
          contextEventEmitter.emit('pastedContextChange', clearPayload);
          
          if (typeof document !== 'undefined') {
            try {
              const evt = new CustomEvent('contextHighlightUpdate', { detail: clearPayload });
              document.dispatchEvent(evt);
            } catch {}
          }
          
          log.log('[contextStore._applyRemoveEntityReference] Cleared highlights');
        } catch (error) {
          console.warn('[contextStore._applyRemoveEntityReference] Failed to clear highlights:', error);
        }
      }
      
      // Mark local modification
      get()._markLocalModification();
      
      // Persist
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyRemoveEntityReference] ✅ Document reference removed');
    },
    
    _promoteToContextDoc: (llmId: string) => {
      set((state) => ({
        entityReferences: state.entityReferences.map(ref =>
          ref.llmId === llmId
            ? { ...ref, documentRole: 'contextDoc' as const }
            : ref
        )
      }));
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      log.log('[contextStore._promoteToContextDoc] Promoted to contextDoc:', llmId);
    },

    _promoteEntityContextToManual: (entityId: string) => {
      set((state) => ({
        entityContexts: state.entityContexts.map(ctx =>
          ctx.entityId === entityId
            ? { ...ctx, metadata: { ...ctx.metadata, addedBy: 'manual' } }
            : ctx
        )
      }));
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      log.log('[contextStore._promoteEntityContextToManual] Promoted to manual:', entityId);
    },

    _removeAllCurrentDocs: () => {
      const currentDocs = get().entityReferences.filter(ref => ref.documentRole === 'currentDoc');
      
      if (currentDocs.length === 0) {
        log.log('[contextStore._removeAllCurrentDocs] No current docs to remove');
        return; // Nothing to remove
      }
      
      log.log('[contextStore._removeAllCurrentDocs] Removing current docs:', {
        count: currentDocs.length,
        llmIds: currentDocs.map(d => d.llmId),
        titles: currentDocs.map(d => d.title)
      });
      
      set((state) => ({
        entityReferences: state.entityReferences.filter(
          ref => ref.documentRole !== 'currentDoc'
        )
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      const remainingRefs = get().entityReferences;
      log.log('[contextStore._removeAllCurrentDocs] ✅ Current docs removed, remaining:', {
        total: remainingRefs.length,
        contextDocs: remainingRefs.filter(r => r.documentRole === 'contextDoc').map(r => r.llmId)
      });
    },
    
    /**
     * ✅ PRESENTATION-SPECIFIC: Update slide information for a presentation
     * Called when user navigates between slides within an already-open presentation
     */
    updatePresentationSlide: (entityId: string, currentSlide: number, totalSlides: number) => {
      log.log('[contextStore.updatePresentationSlide] Updating slide info:', {
        entityId,
        currentSlide,
        totalSlides
      });
      
      // Find the document reference by looking up the entity's llmId
      const entity = useEntityStore.getState().entities.find((e: any) => 
        e._id === entityId || e.id === entityId
      );
      
      if (!entity?.llmId) {
        console.warn('[contextStore.updatePresentationSlide] Entity not found or missing llmId:', entityId);
        return;
      }
      
      // Find and update the document reference
      set((state) => ({
        entityReferences: state.entityReferences.map(ref => 
          ref.llmId === entity.llmId && ref.documentRole === 'currentDoc'
            ? { ...ref, currentSlide, totalSlides }
            : ref
        )
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore.updatePresentationSlide] ✅ Slide info updated for:', entity.llmId);
    },
    
    /**
     * ✅ REFACTOR: Single entry point for active window changes
     * Handles all the logic for updating context when activeWindowId changes
     */
    _handleActiveWindowChange: (windowId: string | null, isRetry: boolean = false) => {
      log.log('[contextStore._handleActiveWindowChange] Active window changed:', windowId, isRetry ? '(retry)' : '');

      // Clear automatic entity context contributions (e.g. AppBuilder) on every window
      // transition. The newly-active app re-contributes its own context via its own
      // subscription, which fires synchronously in the same Zustand update cycle.
      // Skip on retry: the initial call already cleared these, and by retry time the
      // active app has re-contributed its context — clearing again would wipe it.
      if (!isRetry) {
        const automaticContextIds = get().entityContexts
          .filter((ctx: any) => ctx.metadata?.addedBy === 'automatic')
          .map((ctx: any) => ctx.entityId);
        for (const id of automaticContextIds) {
          get()._applyRemoveEntityContext(id);
        }
      }

      // CASE 1: No active window → Clear all currentDoc
      if (!windowId) {
        get()._removeAllCurrentDocs();
        log.log('[contextStore._handleActiveWindowChange] No active window, cleared currentDoc');
        return;
      }
      
      // Get the window from spaceStore
      const window = useSpaceStore.getState().windows[windowId];
      if (!window) {
        console.warn('[contextStore._handleActiveWindowChange] Window not found:', windowId);
        
        // Retry once after a delay (handles page load race condition)
        if (!isRetry) {
          log.log('[contextStore._handleActiveWindowChange] Scheduling retry after 500ms...');
          setTimeout(() => {
            const currentActiveWindowId = useSpaceStore.getState().activeWindowId;
            if (currentActiveWindowId !== windowId) {
              log.log('[contextStore._handleActiveWindowChange] Retry abandoned — activeWindowId changed during delay:', {
                retryWindowId: windowId,
                currentActiveWindowId
              });
              return;
            }
            const retryWindow = useSpaceStore.getState().windows[windowId];
            if (retryWindow) {
              log.log('[contextStore._handleActiveWindowChange] Window found on retry, processing:', windowId);
              get()._handleActiveWindowChange(windowId, true);
            } else {
              console.warn('[contextStore._handleActiveWindowChange] Window still not found after retry:', windowId);
            }
          }, 500);
        }
        return;
      }
      
      // CASE 2: Quick Launcher → Clear all currentDoc
      if (window.appType === 'quicklauncher') {
        get()._removeAllCurrentDocs();
        log.log('[contextStore._handleActiveWindowChange] Quick launcher opened, cleared currentDoc');
        return;
      }

      // CASE 2b: New Window placeholder — no entity, no context, no retry needed
      if ((window.applicationState as any)?.isNew === true) {
        get()._removeAllCurrentDocs();
        log.log('[contextStore._handleActiveWindowChange] New window placeholder, cleared currentDoc');
        return;
      }
      
      // CASE 3: Document window → Replace currentDoc with this document
      const entity = useSpaceStore.getState().getWindowEntity(windowId);
      
      if (!entity?.llmId) {
        log.log('[contextStore._handleActiveWindowChange] Window has no entity/llmId, clearing currentDoc:', {
          windowId,
          hasEntity: !!entity,
          appType: window.appType
        });
        get()._removeAllCurrentDocs();
        return;
      }
      
      log.log('[contextStore._handleActiveWindowChange] 🔍 CONTEXT ELIGIBILITY CHECK:', {
        entityId: entity._id,
        entityType: entity.entityType,
        entityName: entity.name,
        llmId: entity.llmId,
        hasLlmId: !!entity.llmId,
        windowId,
        appType: window.appType
      });
      
      if (!isContextEligibleEntity(entity)) {
        console.warn('[contextStore._handleActiveWindowChange] ❌ Entity NOT eligible for context:', {
          entityType: entity.entityType,
          entityName: entity.name,
          hasLlmId: !!entity.llmId,
          reason: !entity.llmId ? 'Missing llmId' : `Excluded type: ${entity.entityType}`
        });
        get()._removeAllCurrentDocs();
        return;
      }
      
      // Get the appropriate app type for this entity (works for ALL entity types)
      const contextAppType = getContextAppType(entity);
      
      log.log('[contextStore._handleActiveWindowChange] ✅ ENTITY ELIGIBLE FOR CONTEXT:', {
        entityType: entity.entityType,
        entityName: entity.name,
        llmId: entity.llmId,
        contextAppType,
        willAddToContext: true
      });
      
      // Check if this entity is already the currentDoc
      const existingCurrentDoc = get().entityReferences.find(
        ref => ref.documentRole === 'currentDoc' && ref.llmId === entity.llmId
      );
      
      if (existingCurrentDoc) {
        log.log('[contextStore._handleActiveWindowChange] Entity already current, no change needed:', {
          llmId: entity.llmId,
          title: entity.name,
          entityType: entity.entityType
        });
        return;
      }
      
      log.log('[contextStore._handleActiveWindowChange] Replacing context with new entity:', {
        newEntity: entity.name,
        newLlmId: entity.llmId,
        entityType: entity.entityType
      });
      // updatePresentationSlide() is called on every slide change so the existing ref may already
      // carry up-to-date slide info — preserve it so the replacement ref starts with real data.
      let slideInfo: { currentSlide?: number; totalSlides?: number } = {};
      const existingRef = get().entityReferences.find(
        ref => ref.documentRole === 'currentDoc' && ref.llmId === entity.llmId
      );
      if (existingRef && (existingRef as any).currentSlide !== undefined) {
        slideInfo = {
          currentSlide: (existingRef as any).currentSlide,
          totalSlides: (existingRef as any).totalSlides,
        };
      }

      // Remove all existing currentDocs
      get()._removeAllCurrentDocs();
      get()._applyAddEntityReference(entity.llmId, {
        title: entity.name || 'Untitled',
        appType: contextAppType,
        entityType: entity.entityType,
        windowId,
        documentRole: 'currentDoc',
        isActive: true,
        ...slideInfo
      });
      
      const finalRefs = get().entityReferences;
      log.log('[contextStore._handleActiveWindowChange] ✅ SUCCESSFULLY ADDED TO CONTEXT:', {
        llmId: entity.llmId,
        title: entity.name,
        entityType: entity.entityType,
        appType: contextAppType,
        documentRole: 'currentDoc',
        isActive: true,
        windowId,
        totalDocRefsNow: finalRefs.length,
        currentDocs: finalRefs.filter(r => r.documentRole === 'currentDoc').map(r => ({ 
          llmId: r.llmId, 
          title: r.title,
          entityType: r.entityType,
          appType: r.appType 
        })),
        contextDocs: finalRefs.filter(r => r.documentRole === 'contextDoc').map(r => ({ 
          llmId: r.llmId, 
          title: r.title,
          entityType: r.entityType,
          appType: r.appType 
        }))
      });
    },
    
    _applyAddExplicitContent: (content) => {
      const id = `explicit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newContent: ExplicitContent = {
        id,
        ...content,
        clearAfterSubmit: content.clearAfterSubmit ?? true, // Default to true
        addedAt: Date.now()
      };
      
      log.log('[contextStore._applyAddExplicitContent] Adding explicit content:', id);
      
      set((state) => ({
        explicitContent: [...state.explicitContent, newContent]
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyAddExplicitContent] ✅ Explicit content added');
      return id;
    },
    
    _applyRemoveExplicitContent: (id: string) => {
      log.log('[contextStore._applyRemoveExplicitContent] Removing explicit content:', id);
      
      set((state) => ({
        explicitContent: state.explicitContent.filter(item => item.id !== id)
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyRemoveExplicitContent] ✅ Explicit content removed');
    },
    
    _applyAddEntityContext: (entityContext) => {
      log.log('[contextStore._applyAddEntityContext] Adding entity context:', entityContext.entityId);
      
      // Check duplicates
      if (get().hasEntityContext(entityContext.entityId)) {
        log.log('[contextStore._applyAddEntityContext] Duplicate detected, skipping');
        return;
      }
      
      const newContext: EntityContext = {
        ...entityContext,
        metadata: entityContext.metadata || {
          addedBy: 'manual'
        },
        addedAt: new Date().toISOString()
      };
      
      set((state) => ({
        entityContexts: [...state.entityContexts, newContext]
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyAddEntityContext] ✅ Entity context added');
    },
    
    _applyRemoveEntityContext: (entityId: string) => {
      log.log('[contextStore._applyRemoveEntityContext] Removing entity context:', entityId);
      
      set((state) => ({
        entityContexts: state.entityContexts.filter(ctx => ctx.entityId !== entityId)
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyRemoveEntityContext] ✅ Entity context removed');
    },

    setEntityContextContribution: (entityId, contribution) => {
      const existing = get().entityContexts.find(ctx => ctx.entityId === entityId);
      if (existing) {
        // Preserve the original addedBy if it was manual — automatic contributions
        // must not downgrade a user's explicit pin to automatic, which would cause
        // it to be cleared on the next window transition.
        const preservedAddedBy = existing.metadata?.addedBy === 'manual' ? 'manual' : (contribution.metadata?.addedBy ?? existing.metadata?.addedBy);
        set((state) => ({
          entityContexts: state.entityContexts.map(ctx =>
            ctx.entityId === entityId
              ? {
                  ...ctx,
                  ...contribution,
                  metadata: { ...ctx.metadata, ...(contribution.metadata || {}), addedBy: preservedAddedBy }
                }
              : ctx
          )
        }));
        get()._markLocalModification();
        log.log('[contextStore.setEntityContextContribution] Updated entity context:', entityId, { preservedAddedBy });
      } else {
        get()._applyAddEntityContext({
          entityId,
          ...contribution,
          metadata: { addedBy: 'automatic', ...(contribution.metadata || {}) }
        });
        log.log('[contextStore.setEntityContextContribution] Added entity context:', entityId);
      }
    },

    clearEntityContextContribution: (entityId: string) => {
      const existing = get().entityContexts.find(ctx => ctx.entityId === entityId);
      if (existing?.metadata?.addedBy === 'manual') {
        log.log('[contextStore.clearEntityContextContribution] Skipping removal — entry was manually added:', entityId);
        return;
      }
      get()._applyRemoveEntityContext(entityId);
    },
    
    _applyAddFileContext: (fileContext) => {
      log.log('[contextStore._applyAddFileContext] Adding file context:', fileContext.fileName);
      
      const newContext: FileContext = {
        ...fileContext,
        metadata: fileContext.metadata || {
          source: 'upload',
          lastModified: Date.now(),
          addedBy: 'manual'
        },
        addedAt: new Date().toISOString()
      };
      
      set((state) => ({
        fileContexts: [...state.fileContexts, newContext]
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyAddFileContext] ✅ File context added');
    },
    
    _applyRemoveFileContext: (fileName: string) => {
      log.log('[contextStore._applyRemoveFileContext] Removing file context:', fileName);
      
      set((state) => ({
        fileContexts: state.fileContexts.filter(ctx => ctx.fileName !== fileName)
      }));
      
      get()._markLocalModification();
      persistContextToSpaceStore(get);
      
      log.log('[contextStore._applyRemoveFileContext] ✅ File context removed');
    },
    
    // Actions layer (loaded dynamically to avoid circular deps)
    get actions() {
      return createContextActions(get, set);
    }
  };
});

// Expose the store to global scope for tool access only
if (typeof window !== 'undefined') {
  (window as any).__contextStore = useContextStore;
}

/**
 * ✅ REFACTOR: Simplified subscription handler
 * ONLY handles space ID changes - clears context when switching spaces
 * Active window changes are now handled by dedicated subscription
 */
const processSpaceStoreChanges = async (
  state: any, 
  prevState: any, 
  get: () => any, 
  set: (fn: (state: any) => any) => void
) => {
  // PERFORMANCE FIX: Skip processing during window dragging
  if ((window as any).__windowDragging) {
    return;
  }
  
  // ONLY handle space ID changes
  if (state.activeSpace && state.activeSpace.id !== prevState?.activeSpace?.id) {
    log.log('[ContextStore] Space changed, clearing all context:', {
      oldSpace: prevState?.activeSpace?.id,
      newSpace: state.activeSpace.id
    });
    
    const contextStore = get();
    contextStore.clearAllContext();
  }
};

// Initialize the subscription to spaceStore after both stores are ready
let subscriptionInitialized = false;
export const initializeContextStoreSubscription = () => {
  // Prevent multiple initializations
  if (subscriptionInitialized) {
    log.log('[ContextStore] Subscription already initialized, skipping duplicate');
    return;
  }
  subscriptionInitialized = true;
  
  const { subscribe } = useSpaceStore;
  // This follows the proper data flow: entityStore updates → contextStore reacts
  
  // Track previous entities to detect changes
  let prevEntitiesMap = new Map<string, { name: string; llmId: string }>();
  
  useEntityStore.subscribe((state: any) => {
    const contextStore = useContextStore.getState();
    const entityReferences = contextStore.entityReferences;
    const entityContexts = contextStore.entityContexts;
    const fileContexts = contextStore.fileContexts;
    
    let hasUpdates = false;
    
    // Build map of current entities
    const currentEntitiesMap = new Map<string, { name: string; llmId: string }>();
    state.entities.forEach((entity: any) => {
      if (entity._id && entity.name) {
        currentEntitiesMap.set(entity._id, { name: entity.name, llmId: entity.llmId });
      }
    });
    
    // Check for entity changes
    for (const [entityId, current] of currentEntitiesMap.entries()) {
      const prev = prevEntitiesMap.get(entityId);
      
      // Entity name changed
      if (prev && prev.name !== current.name) {
        // Update document reference titles (lookup by llmId)
        const refIndex = entityReferences.findIndex(ref => ref.llmId === current.llmId);
        if (refIndex >= 0) {
          const oldTitle = entityReferences[refIndex].title;
          if (oldTitle !== current.name) {
            log.log('[ContextStore] Updating document reference title from entityStore:', {
              llmId: current.llmId,
              oldTitle,
              newTitle: current.name
            });
            
            const updatedRefs = [...entityReferences];
            updatedRefs[refIndex] = {
              ...updatedRefs[refIndex],
              title: current.name
            };
            
            useContextStore.setState({ entityReferences: updatedRefs });
            log.log('[ContextStore] ✅ Updated document reference title');
            hasUpdates = true;
          }
        }
        
        // Update entity context names (lookup by entityId)
        const entityIndex = entityContexts.findIndex(ctx => ctx.entityId === entityId);
        if (entityIndex >= 0) {
          const oldName = entityContexts[entityIndex].name;
          if (oldName !== current.name) {
            log.log('[ContextStore] Updating entity context name from entityStore:', {
              entityId,
              oldName,
              newName: current.name
            });
            
            const updatedContexts = [...entityContexts];
            updatedContexts[entityIndex] = {
              ...updatedContexts[entityIndex],
              name: current.name
            };
            
            useContextStore.setState({ entityContexts: updatedContexts });
            log.log('[ContextStore] ✅ Updated entity context name');
            hasUpdates = true;
          }
        }
      }
      
      // NEW: llmId assigned (PDF enrichment complete)
      if (!prev?.llmId && current.llmId) {
        const fileContext = fileContexts.find(
          (ctx) => ctx.metadata.entityId === entityId && !ctx.metadata.llmId
        );
        
        if (fileContext) {
          log.info('[ContextStore] 📝 PDF enrichment completed, updating fileContext with llmId:', {
            entityId,
            entityName: current.name,
            llmId: current.llmId,
            fileName: fileContext.fileName
          });
          
          contextStore.updateFileContextWithLlmId(entityId, current.llmId);
          hasUpdates = true;
        }
      }
    }
    
    // Also update explicit content source document titles
    for (const [entityId, current] of currentEntitiesMap.entries()) {
      const prev = prevEntitiesMap.get(entityId);
      
      if (prev && prev.name !== current.name) {
        const explicitContent = contextStore.explicitContent;
        const explicitContentToUpdate = explicitContent.filter(item => 
          item.sourceDocument?.llmId === current.llmId
        );
        
        if (explicitContentToUpdate.length > 0) {
          log.log('[ContextStore] Updating explicit content source document titles from entityStore:', {
            llmId: current.llmId,
            count: explicitContentToUpdate.length,
            oldTitle: prev.name,
            newTitle: current.name
          });
          
          const updatedExplicitContent = explicitContent.map(item => {
            if (item.sourceDocument?.llmId === current.llmId) {
              return {
                ...item,
                sourceDocument: item.sourceDocument ? {
                  llmId: item.sourceDocument.llmId,
                  title: current.name,
                  nodeRange: item.sourceDocument.nodeRange
                } : undefined
              } as ExplicitContent;
            }
            return item;
          });
          
          useContextStore.setState({ explicitContent: updatedExplicitContent });
          log.log('[ContextStore] ✅ Updated explicit content source document titles');
          hasUpdates = true;
        }
      }
    }
    // Race condition: spaceStore often sets activeWindowId BEFORE entityStore has loaded
    // entities. When that happens, _handleActiveWindowChange calls getWindowEntity which
    // returns null (entityStore not initialized), clears currentDoc, and returns — so the
    // active document is never set. Re-trigger it here once entities are first available.
    if (prevEntitiesMap.size === 0 && currentEntitiesMap.size > 0) {
      const hasCurrentDoc = useContextStore.getState().entityReferences.some(
        ref => ref.documentRole === 'currentDoc'
      );
      if (!hasCurrentDoc) {
        const activeWindowId = useSpaceStore.getState().activeWindowId;
        if (activeWindowId) {
          log.log('[ContextStore] 🔄 EntityStore first load — re-evaluating active window context for:', activeWindowId);
          // Use isRetry=true: the initial (failed) call already cleared automatic contexts,
          // and any apps that re-contributed context since then should not be disturbed.
          // We only need to set currentDoc — no need to re-clear automatic contexts.
          useContextStore.getState()._handleActiveWindowChange(activeWindowId, true);
        }
      }
    }

    prevEntitiesMap = currentEntitiesMap;
  });
  // This is the SINGLE ENTRY POINT for automatic context updates
  // PERFORMANCE FIX: Only process when activeWindowId or spaceId actually changes
  let lastActiveWindowId: string | null | undefined = null;
  let lastSpaceId: string | null | undefined = null;
    
  subscribe((state, prevState) => {
    // PERFORMANCE: Early return if neither activeWindowId nor spaceId changed
    const activeWindowId = state.activeWindowId;
    const spaceId = state.activeSpace?.id;
    const prevSpaceId = prevState?.activeSpace?.id;
    
    const activeWindowChanged = activeWindowId !== lastActiveWindowId;
    const spaceChanged = spaceId !== lastSpaceId;
    
    if (!activeWindowChanged && !spaceChanged) {
      return; // No changes we care about, skip processing entirely
    }
    
    log.log(formatJSON({
      activeWindowId: state.activeWindowId,
      prevActiveWindowId: lastActiveWindowId,
      spaceId: state.activeSpace?.id,
      prevSpaceId: lastSpaceId
    }, '[ContextStore] 🔔 Subscription fired'));
    
    // 1. Handle space changes (clear all context)
    if (spaceChanged) {
      const contextStoreApi = useContextStore.getState;
      const contextStoreSet = useContextStore.setState;
      const prevFullState = { activeSpace: { id: lastSpaceId } };
      processSpaceStoreChanges(state, prevFullState, contextStoreApi, contextStoreSet).catch(error => {
        console.error('[ContextStore] Error processing space store changes:', error);
      });
      lastSpaceId = spaceId;
    }
    
    // 2. Handle active window changes (single entry point)
    if (activeWindowChanged) {
      lastActiveWindowId = activeWindowId;
      
      // Call the single handler for active window changes
      const contextStore = useContextStore.getState();
      contextStore._handleActiveWindowChange(activeWindowId);
    }
  });
  
  log.log('[ContextStore] ✅ Subscriptions initialized (entityStore + spaceStore)');
};

// ─── Space Y.Doc chatContext sync ─────────────────────────────────────────────
//
// Writes contextStore state into `space:{spaceId}` Y.Doc under Y.Map('chatContext')
// whenever the relevant fields change. This makes chat context available to:
//   • The headless agent loop (reads the same Y.Doc)
//   • Other browser tabs (cross-tab context sync — also fixes the P2.OPEN context bug)
//
// Uses a Zustand subscription so mutations don't need to be modified individually.
// Debounced at 200ms to avoid flooding the Y.Doc on rapid changes.

let _chatContextYDocObserverCleanup: (() => void) | null = null;

function _writeChatContextToYDoc(state: ReturnType<typeof useContextStore.getState>) {
  const spaceId = state.context?.spaceId;
  if (!spaceId) return;

  try {
    const { getCRDTServiceInstance } = require('../../../../services/crdt/crdtServiceWS');
    const crdt = getCRDTServiceInstance();
    const ydoc = crdt.getYDoc(`space:${spaceId}`);
    if (!ydoc) return;

    const chatContextMap = ydoc.getMap('chatContext');

    // Equality check before writing to avoid feedback loops.
    // Write to both keys during migration so older tabs still receive updates.
    const newDocRefs = JSON.stringify(state.entityReferences ?? []);
    if (chatContextMap.get('entityReferences') !== newDocRefs) {
      chatContextMap.set('entityReferences', newDocRefs);
      chatContextMap.set('documentReferences', newDocRefs); // backward-compat for older tabs
    }
    const newEntityCtx = JSON.stringify(state.entityContexts ?? []);
    if (chatContextMap.get('entityContexts') !== newEntityCtx) {
      chatContextMap.set('entityContexts', newEntityCtx);
    }
    const newWebsiteCtx = JSON.stringify((state as any).websiteContexts ?? []);
    if (chatContextMap.get('websiteContexts') !== newWebsiteCtx) {
      chatContextMap.set('websiteContexts', newWebsiteCtx);
    }
    const newWebSearch = JSON.stringify(state.webSearchEnabled ?? true);
    if (chatContextMap.get('webSearchEnabled') !== newWebSearch) {
      chatContextMap.set('webSearchEnabled', newWebSearch);
    }
    const newRules = JSON.stringify((state as any).userRules ?? []);
    if (chatContextMap.get('userRules') !== newRules) {
      chatContextMap.set('userRules', newRules);
    }
    // Explicit content (pasted text, text selections) — needed by the headless runner
    // to include user-provided content in the user message just as the browser does.
    const explicitForSync = (state.explicitContent ?? []).map((c: any) => ({
      sourceType: c.sourceType,
      text: c.text,
      metadata: c.metadata,
      sourceDocument: c.sourceDocument
        ? { title: c.sourceDocument.title, llmId: c.sourceDocument.llmId }
        : undefined,
    }));
    const newExplicit = JSON.stringify(explicitForSync);
    if (chatContextMap.get('explicitContent') !== newExplicit) {
      chatContextMap.set('explicitContent', newExplicit);
    }
  } catch {
    // Non-fatal — Y.Doc may not be ready yet; next mutation will retry
  }
}

let _chatContextWriteTimer: ReturnType<typeof setTimeout> | null = null;
function _debouncedChatContextWrite(state: ReturnType<typeof useContextStore.getState>) {
  if (_chatContextWriteTimer) clearTimeout(_chatContextWriteTimer);
  _chatContextWriteTimer = setTimeout(() => _writeChatContextToYDoc(state), 200);
}

/**
 * Set up the chatContext Y.Doc observer for a new space.
 * Called from setActiveSpace() so each space gets its own observer.
 */
export function initChatContextYDocSync(spaceId: string) {
  // Tear down previous observer
  if (_chatContextYDocObserverCleanup) {
    _chatContextYDocObserverCleanup();
    _chatContextYDocObserverCleanup = null;
  }

  if (!spaceId) return;

  try {
    const { getCRDTServiceInstance } = require('../../../../services/crdt/crdtServiceWS');
    const crdt = getCRDTServiceInstance();
    const ydoc = crdt.getYDoc(`space:${spaceId}`);
    if (!ydoc) return;

    const chatContextMap = ydoc.getMap('chatContext');

    // Subscribe to Y.Doc chatContext changes → update Zustand
    // Only applies changes from OTHER tabs (local writes are equality-checked above)
    const observer = () => {
      const current = useContextStore.getState();

      // Read new key first; fall back to old key for sessions written before the rename.
      const docRefsRaw = chatContextMap.get('entityReferences') || chatContextMap.get('documentReferences');
      if (docRefsRaw) {
        try {
          const refs = JSON.parse(docRefsRaw);
          if (JSON.stringify(refs) !== JSON.stringify(current.entityReferences)) {
            useContextStore.setState({ entityReferences: refs });
            log.log('[ContextStore←YDoc] entityReferences updated from Y.Doc', { count: refs.length });
          }
        } catch { /* skip malformed */ }
      }

      const entityCtxRaw = chatContextMap.get('entityContexts');
      if (entityCtxRaw) {
        try {
          const contexts = JSON.parse(entityCtxRaw);
          if (JSON.stringify(contexts) !== JSON.stringify(current.entityContexts)) {
            useContextStore.setState({ entityContexts: contexts });
          }
        } catch { /* skip malformed */ }
      }

      const webSearchRaw = chatContextMap.get('webSearchEnabled');
      if (webSearchRaw !== undefined) {
        try {
          const enabled = JSON.parse(webSearchRaw);
          if (enabled !== current.webSearchEnabled) {
            useContextStore.setState({ webSearchEnabled: enabled });
          }
        } catch { /* skip malformed */ }
      }
    };

    chatContextMap.observe(observer);
    _chatContextYDocObserverCleanup = () => chatContextMap.unobserve(observer);
    log.log('[ContextStore] Space Y.Doc chatContext observer attached for space:', spaceId);
  } catch {
    // Non-fatal — Y.Doc may not be ready (e.g. first load before WS connects)
  }
}

// Subscribe to contextStore state changes and write to Y.Doc
useContextStore.subscribe((state, prevState) => {
  const spaceId = state.context?.spaceId;
  if (!spaceId) return;

  const docRefsChanged = state.entityReferences !== prevState.entityReferences;
  const entityCtxChanged = state.entityContexts !== prevState.entityContexts;
  const webSearchChanged = state.webSearchEnabled !== prevState.webSearchEnabled;
  const websiteCtxChanged = (state as any).websiteContexts !== (prevState as any).websiteContexts;
  const userRulesChanged = (state as any).userRules !== (prevState as any).userRules;

  if (docRefsChanged || entityCtxChanged || webSearchChanged || websiteCtxChanged || userRulesChanged) {
    _debouncedChatContextWrite(state);
    log.log('[ContextStore→YDoc] chatContext change detected, scheduling write');
  }
});

